import { Icon, ScrollArea, Switch, token } from "@may-i/mash-design-system";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import { useQueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import type { CameraMemo } from "~/entities/camera-memo/types";
import type { Camera } from "~/entities/camera/types";
import { hasRealSnapshot, isCameraAnalyzed } from "~/entities/camera/utils";
import type {
  AnalysisConfig,
  CountingLine,
  CountingLineEventType,
  IntervalZone,
  IntervalZoneEventType,
} from "~/entities/analysis-config/types";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { BRANCHES_QUERY_KEY, useBranches } from "~/features/branch/hooks/use-branches";
import { BranchConfigStatus } from "~/entities/branch/types";
import { BranchConfigStatus as LegacyBranchConfigStatus } from "~/_legacy/types/common";
import { updateBranchConfig } from "~/_legacy/apis/update-branch-config";
import { useUndoStack } from "~/shared/hooks/use-undo-stack";
import { useUnsavedChangesGuard } from "~/shared/hooks/use-unsaved-changes-guard";
import { compareCamId } from "~/shared/lib/compare-cam-id";
import { ANALYSIS_CONFIGS_QUERY_KEY, useAnalysisConfigs } from "~/features/analysis-config/hooks/use-analysis-configs";
import {
  useUpdateCountingLines,
  useUpdateIntervalZones,
  useGenerateKeys,
} from "~/features/analysis-config/hooks/use-analysis-config-mutations";
import { useUpdateCameraMemos } from "~/features/camera-memo/hooks/use-camera-memo-mutations";
import { useCameraMemos } from "~/features/camera-memo/hooks/use-camera-memos";
import { CAMERAS_QUERY_KEY, useCameras } from "~/features/camera/hooks/use-cameras";
import { useUpdateCameraAnalysisNumber } from "~/features/camera/hooks/use-camera-mutations";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { useUpdateFloorPlan } from "~/features/floor-plan/hooks/use-floor-plan-mutations";
import { useFloorPlans } from "~/features/floor-plan/hooks/use-floor-plans";
import { sendAnalysisSketchSubmittedMessageToSlack } from "~/_legacy/utils/external";
import { useGetFloorPlanMemoDiagramsGroupedByFloorPlan } from "~/_legacy/data-hooks/use-get-floor-plan-memo-diagrams-grouped-by-floor-plan";
import { useGetFloorPlanMemoTextsGroupedByFloorPlan } from "~/_legacy/data-hooks/use-get-floor-plan-memo-texts-grouped-by-floor-plan";
import { useToast } from "~/shared/ui/toast";
import { CameraCard } from "./CameraCard";
import { DEFAULT_CAMERA_FILTER, type CameraFilterState, type EventTypeAvailability } from "./CameraFilterPopover";
import { CanvasToolbar, type BothLayout, type ViewMode } from "./CanvasToolbar";
import { DraftActionBar } from "./DraftActionBar";
import { FloorPlanViewer, type FloorPlanDrawingTarget } from "./FloorPlanViewer";
import { ClearAllKeysDialog } from "./ClearAllKeysDialog";
import { DeleteLineZoneDialog, type DeleteLineZoneTarget } from "./DeleteLineZoneDialog";
import { KeyValidationDialog } from "./KeyValidationDialog";
import { KeyGenLoadingOverlay } from "./KeyGenLoadingOverlay";
import { AddAdjustGuideModal } from "./add-adjust-guide/AddAdjustGuideModal";
import { type MemoAction, MemoPanel } from "~/pages/camera-mapping/MemoPanel";
import { IcPickOnImg } from "~/shared/assets/icons/IcPickOnImg";
import { AllMemosPanel } from "./AllMemosPanel";
import { InlineMemoSection } from "./InlineMemoSection";
import { SidebarHeader } from "./SidebarHeader";
import { SnapshotCanvas, type DrawingRequest } from "./SnapshotCanvas";

let nextLocalId = -1;
function getNextLocalId() {
  return nextLocalId--;
}

// 좌표 비교 epsilon — percent(0~100) 단위 좌표 기준. 서버 직렬화 precision/부동소수점 오차 흡수.
const COORD_EPSILON = 1e-6;

function coordsEqual(a: { x: number; y: number }[], b: { x: number; y: number }[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i].x - b[i].x) > COORD_EPSILON || Math.abs(a[i].y - b[i].y) > COORD_EPSILON) {
      return false;
    }
  }
  return true;
}

function lineDiffersFromServer(local: CountingLine, server: CountingLine): boolean {
  if (local.key !== server.key) return true;
  // analysisConfig
  const la = local.analysisConfig, sa = server.analysisConfig;
  if ((la == null) !== (sa == null)) return true;
  if (la && sa) {
    if (la.name !== sa.name) return true;
    if (la.eventType !== sa.eventType) return true;
    if (!coordsEqual(la.coordinates, sa.coordinates)) return true;
  }
  // floorPlanConfig
  const lf = local.floorPlanConfig, sf = server.floorPlanConfig;
  if ((lf == null) !== (sf == null)) return true;
  if (lf && sf && !coordsEqual(lf.coordinates, sf.coordinates)) return true;
  return false;
}

function zoneDiffersFromServer(local: IntervalZone, server: IntervalZone): boolean {
  if (local.key !== server.key) return true;
  const la = local.analysisConfig, sa = server.analysisConfig;
  if ((la == null) !== (sa == null)) return true;
  if (la && sa) {
    if (la.name !== sa.name) return true;
    if (la.eventType !== sa.eventType) return true;
    if (!coordsEqual(la.coordinates, sa.coordinates)) return true;
  }
  const lf = local.floorPlanConfig, sf = server.floorPlanConfig;
  if ((lf == null) !== (sf == null)) return true;
  if (lf && sf && !coordsEqual(lf.coordinates, sf.coordinates)) return true;
  return false;
}

export function AnalysisDraftPage() {
  const { t } = useTranslation("analysis-draft");
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;
  const { branchUid } = useParams();
  const { selectedVersion } = useSelectedVersion();
  const versionId = selectedVersion?.id;
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: branches = [] } = useBranches();
  const clientUid = branches.find((b) => b.uid === branchUid)?.client.uid;

  const { data: cameras = [], isLoading: isCamerasLoading } = useCameras(branchUid, versionId);
  const { data: floorPlans = [], isLoading: isFloorPlansLoading } = useFloorPlans(branchUid, versionId);
  const { data: serverConfigs = [], isFetched: isConfigsFetched, isLoading: isConfigsLoading } = useAnalysisConfigs(branchUid, versionId);
  const { data: cameraMemos = [] } = useCameraMemos(branchUid, versionId);

  const floorPlanIdsForMemo = useMemo(() => floorPlans.map((fp) => fp.id), [floorPlans]);
  const { data: floorPlanMemoDiagramsByFp = {} } =
    useGetFloorPlanMemoDiagramsGroupedByFloorPlan({
      branchUid,
      branchVersionId: versionId,
      floorPlanIds: floorPlanIdsForMemo,
    });
  const { data: floorPlanMemoTextsByFp = {} } =
    useGetFloorPlanMemoTextsGroupedByFloorPlan({
      branchUid,
      branchVersionId: versionId,
      floorPlanIds: floorPlanIdsForMemo,
    });
  const updateCameraMemosMut = useUpdateCameraMemos(branchUid!, versionId);

  // 페이지 마운트 시 캐시를 무효화하여 최신 데이터를 가져옴
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [ANALYSIS_CONFIGS_QUERY_KEY, branchUid, versionId] });
    queryClient.invalidateQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
  }, [queryClient, branchUid, versionId]);

  // 로컬 편집 상태 — serverConfigs를 기반으로 초기화하고 로컬에서 라인/구역을 추가/삭제/수정
  const [localConfigs, setLocalConfigs] = useState<AnalysisConfig[]>([]);
  const serverConfigsRef = useRef<AnalysisConfig[] | null>(null);
  const isSavingRef = useRef(false);
  // 6차 an: 현재 좌표 편집 중인 캔버스의 finishEdit 콜백. 다른 캔버스에서 클릭 시 이걸 호출 → cross-canvas 자동 종료.
  // 8차: 편집 중 저장 차단. 양쪽 캔버스가 편집 진입 시 .current=closure / 종료 시 =null 로 일관 세팅하는 불변식에 piggyback —
  //      ref 를 setter 래퍼로 감싸 할당 자체가 isCoordinateEditing state 를 갱신한다 (캔버스 코드는 변경 없음).
  const [isCoordinateEditing, setIsCoordinateEditing] = useState(false);
  const coordinateEditingFinishValueRef = useRef<(() => void) | null>(null);
  const coordinateEditingFinishHandleRef = useRef<MutableRefObject<(() => void) | null> | null>(null);
  if (coordinateEditingFinishHandleRef.current === null) {
    coordinateEditingFinishHandleRef.current = {
      get current() {
        return coordinateEditingFinishValueRef.current;
      },
      set current(value: (() => void) | null) {
        coordinateEditingFinishValueRef.current = value;
        setIsCoordinateEditing(value !== null);
      },
    };
  }
  const coordinateEditingFinishRef = coordinateEditingFinishHandleRef.current;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const undoStack = useUndoStack(
    useMemo(() => ({ localConfigs: setLocalConfigs }), []),
  );
  const localConfigsRef = useRef(localConfigs);
  localConfigsRef.current = localConfigs;
  const pushUndo = useCallback(() => {
    undoStack.push({ localConfigs: localConfigsRef.current });
  }, [undoStack]);

  useEffect(() => {
    if (isSavingRef.current) return;
    if (isConfigsFetched && serverConfigs !== serverConfigsRef.current) {
      // 서버에서 analysisNumber가 null인 카메라는 로컬의 "N" 번호를 보존하거나 새로 부여
      const prevLocal = localConfigsRef.current;
      const usedNNumbers = new Set<string>();

      // 서버에서 내려온 N 번호 수집 (충돌 방지)
      for (const cfg of serverConfigs) {
        if (cfg.analysisNumber != null && cfg.analysisNumber.startsWith("N")) {
          usedNNumbers.add(cfg.analysisNumber);
        }
      }
      // 기존 로컬의 N 번호도 수집
      for (const prev of prevLocal) {
        if (prev.analysisNumber != null && prev.analysisNumber.startsWith("N")) {
          usedNNumbers.add(prev.analysisNumber);
        }
      }

      const merged = serverConfigs.map((cfg) => {
        if (!isCameraAnalyzed(cfg.analysisNumber)) {
          // 서버에 이미 N 번호가 저장되어 있으면 그대로 사용
          if (cfg.analysisNumber != null && cfg.analysisNumber.startsWith("N")) {
            usedNNumbers.add(cfg.analysisNumber);
            return cfg;
          }
          const prev = prevLocal.find((p) => p.cameraId === cfg.cameraId);
          if (prev?.analysisNumber != null && prev.analysisNumber.startsWith("N")) {
            return { ...cfg, analysisNumber: prev.analysisNumber };
          }
          // 새 미분석 카메라 — 중복 없는 N 번호 부여
          let n = 1;
          while (usedNNumbers.has(`N${n}`)) n++;
          const newNumber = `N${n}`;
          usedNNumbers.add(newNumber);
          return { ...cfg, analysisNumber: newNumber };
        }
        return cfg;
      });
      setLocalConfigs(merged);
      serverConfigsRef.current = serverConfigs;
    }
  }, [serverConfigs, isConfigsFetched]);

  // 도면 선택은 페이지 로컬 상태(이탈 시 자동 리셋). 진입/도면 목록 변경 시 기본값 = 전체 선택
  // (F17) selectedFloorPlanIds = "도면 필터"(어떤 도면 카메라를 볼지, 카메라 필터 팝오버로 이동)
  const [selectedFloorPlanIds, setSelectedFloorPlanIds] = useState<Set<number>>(new Set());
  // (F17) viewerFloorPlanId = "도면 뷰에 띄울 도면"(단일). null 이면 선택 카메라 도면 추종.
  const [viewerFloorPlanId, setViewerFloorPlanId] = useState<number | null>(null);
  // (F16) 마지막 선택 카메라를 sessionStorage 에 저장해 페이지 재진입 시 복원
  const cameraStorageKey = `mst-draft-selectedCamera-${branchUid}-${versionId}`;
  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(cameraStorageKey);
    return stored ? Number(stored) : null;
  });
  useEffect(() => {
    if (selectedCameraId != null) {
      sessionStorage.setItem(cameraStorageKey, String(selectedCameraId));
    }
  }, [selectedCameraId, cameraStorageKey]);
  // 4-1-2: 캔버스 뷰 모드 — 스냅샷만/도면만/둘 다 (기본=스냅샷만)
  const [viewMode, setViewMode] = useState<ViewMode>("snapshot");
  // '둘 다' 모드 배치 방향 (개선 니즈 #4). 가로로 긴 모니터에서 좌우로 놓으면 도면이 커진다.
  // 세션 동안만 기억한다 — 새로고침하면 기본값(위아래)으로 돌아간다.
  const [bothLayout, setBothLayout] = useState<BothLayout>("vertical");
  const isSideBySide = viewMode === "both" && bothLayout === "horizontal";
  const showFloorPlan = viewMode === "floorPlan" || viewMode === "both";
  const showSnapshot = viewMode === "snapshot" || viewMode === "both";
  const [showLineZone, setShowLineZone] = useState(true);
  const [drawingRequest, setDrawingRequest] = useState<DrawingRequest | null>(null);
  // 4-3-2: 양쪽 캔버스(스냅샷/도면) 중 어디서 그리기 시작했는지. 첫 클릭한 쪽이 ownership 잡아 다른 쪽 무시
  const [drawingActiveCanvas, setDrawingActiveCanvas] = useState<"snapshot" | "floorPlan" | null>(null);
  const handleDrawingClaim = useCallback((canvas: "snapshot" | "floorPlan") => {
    setDrawingActiveCanvas(canvas);
  }, []);
  // drawingRequest 가 null 로 바뀔 때 active canvas 도 리셋
  useEffect(() => {
    if (drawingRequest == null) setDrawingActiveCanvas(null);
  }, [drawingRequest]);
  const [selectedEventSource, setSelectedEventSource] = useState<
    { type: "line"; id: number } | { type: "zone"; id: number } | null
  >(null);

  // PO QA 2차 o: TopOverlay (absolute, glass) 가 캔버스 상단을 덮어 이미지가 가려지는 문제.
  // overlay 실제 높이를 측정해 canvas 자식들에 `topPadding` 으로 전달 → 이미지 fit 시 가용 영역 (canvasH - topPadding) 기준 계산.
  // PO QA 2차 o: TopOverlay 높이 측정.
  // callback ref 사용 — 초기 마운트가 spinner(early return)일 때 useEffect([]) 가 빈 ref 를 잡는 문제를 방지.
  const [topOverlayHeight, setTopOverlayHeight] = useState(0);
  const topOverlayRoRef = useRef<ResizeObserver | null>(null);
  const topOverlayRef = useCallback((el: HTMLDivElement | null) => {
    if (topOverlayRoRef.current) {
      topOverlayRoRef.current.disconnect();
      topOverlayRoRef.current = null;
    }
    if (!el) return;
    setTopOverlayHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => {
      setTopOverlayHeight(el.offsetHeight);
    });
    ro.observe(el);
    topOverlayRoRef.current = ro;
  }, []);
  // PO QA 2차 o 후속4: DraftActionBar 도 position:absolute 라 캔버스 하단을 덮음 — 측정해 bottomPadding 으로 전달.
  // 단독 보기: 이미지가 (topPadding ~ canvasH - bottomPadding) 안에 FIT_MARGIN 마진으로 중앙 배치.
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const bottomBarRoRef = useRef<ResizeObserver | null>(null);
  const bottomBarRef = useCallback((el: HTMLDivElement | null) => {
    if (bottomBarRoRef.current) {
      bottomBarRoRef.current.disconnect();
      bottomBarRoRef.current = null;
    }
    if (!el) return;
    setBottomBarHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => {
      setBottomBarHeight(el.offsetHeight);
    });
    ro.observe(el);
    bottomBarRoRef.current = ro;
  }, []);

  // 4-4-2: 캔버스 객체 선택 시 사이드바 카드로 자동 스크롤
  useEffect(() => {
    if (!selectedEventSource) return;
    const key = `${selectedEventSource.type}-${selectedEventSource.id}`;
    // 다음 frame 에 querySelector — 카드가 막 마운트됐을 때도 안전
    const t = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-line-zone-id="${key}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(t);
  }, [selectedEventSource]);

  // 7차 az: 카메라 선택 시 사이드바 카메라 카드로 자동 스크롤 (도면 마커 클릭 등)
  useEffect(() => {
    if (selectedCameraId == null) return;
    const t = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-camera-id="${selectedCameraId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(t);
  }, [selectedCameraId]);
  const [cameraFilter, setCameraFilter] = useState<CameraFilterState>(DEFAULT_CAMERA_FILTER);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteLineZoneTarget | null>(null);
  const [memoOpen, setMemoOpen] = useState(false);
  const [allMemosOpen, setAllMemosOpen] = useState(false);
  const [addAdjustGuideOpen, setAddAdjustGuideOpen] = useState(false);
  const [memoPlacingMode, setMemoPlacingMode] = useState<{ memoId: number; cameraId: number } | null>(null);
  const [selectedMemoId, setSelectedMemoId] = useState<number | null>(null);
  const [floorPlanDrawingTarget, setFloorPlanDrawingTarget] = useState<FloorPlanDrawingTarget | null>(null);
  const [snapshotDrawingTarget, setSnapshotDrawingTarget] = useState<{ type: "line" | "zone"; id: number; name: string } | null>(null);

  // 저장하지 않은 변경사항 감지 — handleSave의 diff 로직과 동일한 기준
  // (자동 N번호 부여는 서버 저장이 아니므로 dirty 아님)
  // 메모는 동작 즉시 서버에 반영되므로(전체 저장과 분리) 여기 판정 대상이 아니다.
  const { t: tShared } = useTranslation("shared");
  const isDirty = useMemo(() => {
    if (!serverConfigsRef.current) return false;

    for (const localCfg of localConfigs) {
      const serverCfg = serverConfigs.find((s) => s.cameraId === localCfg.cameraId);

      const localAN = localCfg.analysisNumber;
      const serverAN = serverCfg?.analysisNumber ?? null;
      if (localAN !== serverAN) {
        const isAutoNAssignment = serverAN === null && localAN != null && localAN.startsWith("N");
        if (!isAutoNAssignment) return true;
      }

      const serverLines = serverCfg?.countingLines ?? [];
      if (localCfg.countingLines.length !== serverLines.length) return true;
      for (const line of localCfg.countingLines) {
        if (line.id < 0) return true;
        const serverLine = serverLines.find((l) => l.id === line.id);
        if (!serverLine) return true;
        if (lineDiffersFromServer(line, serverLine)) return true;
      }

      const serverZones = serverCfg?.intervalZones ?? [];
      if (localCfg.intervalZones.length !== serverZones.length) return true;
      for (const zone of localCfg.intervalZones) {
        if (zone.id < 0) return true;
        const serverZone = serverZones.find((z) => z.id === zone.id);
        if (!serverZone) return true;
        if (zoneDiffersFromServer(zone, serverZone)) return true;
      }
    }
    return false;
  }, [localConfigs, serverConfigs]);
  useUnsavedChangesGuard(isDirty, tShared("unsavedChangesGuard.message"));

  // 분석 대상 카메라
  const analyzedCameras = useMemo(
    () => cameras.filter((c) => isCameraAnalyzed(c.analysisNumber)),
    [cameras],
  );

  // (F17) 카메라가 하나라도 있는 도면 id — 도면 필터에서 대상 없는 도면은 disabled 처리
  const floorPlanIdsWithCameras = useMemo(() => {
    const set = new Set<number>();
    for (const c of cameras) if (c.floorPlanId != null) set.add(c.floorPlanId);
    return set;
  }, [cameras]);

  // 도면에 표시된 카메라
  const mappedCameras = useMemo(
    () => cameras.filter((c) => c.floorPlanId !== null && isCameraAnalyzed(c.analysisNumber)),
    [cameras],
  );

  // 미분석 카메라 넘버링 — 서버에 저장된 N번호는 그대로, null인 카메라만 새 번호 부여
  const nonAnalysisNumberMap = useMemo(() => {
    const map = new Map<number, string>();
    const usedNNumbers = new Set<string>();
    for (const cam of cameras) {
      if (cam.analysisNumber != null && cam.analysisNumber.startsWith("N")) {
        map.set(cam.id, cam.analysisNumber);
        usedNNumbers.add(cam.analysisNumber);
      }
    }
    for (const cam of cameras) {
      if (cam.analysisNumber === null) {
        let n = 1;
        while (usedNNumbers.has(`N${n}`)) n++;
        const num = `N${n}`;
        map.set(cam.id, num);
        usedNNumbers.add(num);
      }
    }
    return map;
  }, [cameras]);

  // 카메라별 analysisConfig 맵
  const configByCameraId = useMemo(() => {
    const map = new Map<number, AnalysisConfig>();
    for (const cfg of localConfigs) {
      map.set(cfg.cameraId, cfg);
    }
    return map;
  }, [localConfigs]);

  // 현재 선택된 도면의 카메라들 — 번호순 정렬
  // 로컬 번호가 유효하면 즉시 반영, 비어있으면 서버 원본 기준 유지
  const camerasOnFloorPlan = useMemo(() => {
    const filtered = cameras.filter((c) => c.floorPlanId != null && selectedFloorPlanIds.has(c.floorPlanId));
    const getSortName = (cam: Camera) => {
      const localNum = configByCameraId.get(cam.id)?.analysisNumber;
      if (localNum != null && localNum.replace(/^N/, "") !== "") return localNum;
      if (cam.analysisNumber != null) return cam.analysisNumber;
      return nonAnalysisNumberMap.get(cam.id) ?? cam.defaultCamId;
    };
    return filtered.sort((a, b) => compareCamId(getSortName(a), getSortName(b)));
  }, [cameras, selectedFloorPlanIds, configByCameraId, nonAnalysisNumberMap]);

  // 필터에 매칭되는 라인/구역 판정
  const isLineMatchingFilter = useCallback(
    (l: CountingLine) => {
      const et = l.analysisConfig?.eventType;
      if (et === "traffic") return cameraFilter.lineTraffic;
      if (et === "visit") return cameraFilter.lineVisit;
      if (et === "default") return cameraFilter.lineDefault;
      return true; // analysisConfig이 null인 라인(도면 전용)은 항상 표시
    },
    [cameraFilter.lineTraffic, cameraFilter.lineVisit, cameraFilter.lineDefault],
  );

  const isZoneMatchingFilter = useCallback(
    (z: IntervalZone) => {
      const et = z.analysisConfig?.eventType;
      if (et === "default") return cameraFilter.zoneDefault;
      if (et === "purchase") return cameraFilter.zonePurchase;
      return true;
    },
    [cameraFilter.zoneDefault, cameraFilter.zonePurchase],
  );

  // 카메라 필터 적용
  const filteredCamerasOnFloorPlan = useMemo(() => {
    return camerasOnFloorPlan.filter((camera) => {
      const cfg = configByCameraId.get(camera.id);
      const isAnalyzed = isCameraAnalyzed(camera.analysisNumber);

      // 분석 여부 필터
      if (isAnalyzed && !cameraFilter.analyzed) return false;
      if (!isAnalyzed && !cameraFilter.notAnalyzed) return false;

      // 이벤트 소스 필터
      const lines = cfg?.countingLines ?? [];
      const zones = cfg?.intervalZones ?? [];
      const hasNoEventSource = lines.length === 0 && zones.length === 0;

      if (hasNoEventSource) return cameraFilter.noEventSource;

      return lines.some(isLineMatchingFilter) || zones.some(isZoneMatchingFilter);
    });
  }, [camerasOnFloorPlan, configByCameraId, cameraFilter, isLineMatchingFilter, isZoneMatchingFilter]);

  // 도면별 그룹핑 (2개 이상의 도면이 선택 결과에 포함되면 섹션 헤더로 노출 — 4-5-4)
  const groupedCamerasByFloorPlan = useMemo(() => {
    const groups = new Map<number, { floorPlan: FloorPlan; cameras: Camera[] }>();
    for (const camera of filteredCamerasOnFloorPlan) {
      if (camera.floorPlanId == null) continue;
      const fp = floorPlans.find((f) => f.id === camera.floorPlanId);
      if (!fp) continue;
      const bucket = groups.get(fp.id);
      if (bucket) bucket.cameras.push(camera);
      else groups.set(fp.id, { floorPlan: fp, cameras: [camera] });
    }
    return Array.from(groups.values()).sort(
      (a, b) => a.floorPlan.floorNumber - b.floorPlan.floorNumber,
    );
  }, [filteredCamerasOnFloorPlan, floorPlans]);

  // 집계 통계
  const summary = useMemo(() => {
    const allLines = localConfigs.flatMap((c) => c.countingLines);
    const allZones = localConfigs.flatMap((c) => c.intervalZones);
    return {
      lineTotalCount: allLines.length,
      lineNormalCount: allLines.filter((l) => l.analysisConfig?.eventType === "default").length,
      lineFloatingCount: allLines.filter((l) => l.analysisConfig?.eventType === "traffic").length,
      lineVisitCount: allLines.filter((l) => l.analysisConfig?.eventType === "visit").length,
      zoneTotalCount: allZones.length,
      zoneNormalCount: allZones.filter((z) => z.analysisConfig?.eventType === "default").length,
      zonePurchaseCount: allZones.filter((z) => z.analysisConfig?.eventType === "purchase").length,
    };
  }, [localConfigs]);

  // 현재 도면 카메라들에 실제 존재하는 이벤트 타입 — 필터 체크박스 disabled 판단용
  const filterAvailability = useMemo<EventTypeAvailability>(() => {
    let hasAnalyzed = false;
    let hasNotAnalyzed = false;
    let hasLineTraffic = false;
    let hasLineVisit = false;
    let hasLineDefault = false;
    let hasZoneDefault = false;
    let hasZonePurchase = false;
    let hasNoEventSource = false;

    for (const camera of camerasOnFloorPlan) {
      const cfg = configByCameraId.get(camera.id);
      if (isCameraAnalyzed(camera.analysisNumber)) hasAnalyzed = true;
      else hasNotAnalyzed = true;

      const lines = cfg?.countingLines ?? [];
      const zones = cfg?.intervalZones ?? [];

      if (lines.length === 0 && zones.length === 0) {
        hasNoEventSource = true;
      }
      for (const l of lines) {
        const et = l.analysisConfig?.eventType;
        if (et === "traffic") hasLineTraffic = true;
        else if (et === "visit") hasLineVisit = true;
        else if (et === "default") hasLineDefault = true;
      }
      for (const z of zones) {
        const et = z.analysisConfig?.eventType;
        if (et === "default") hasZoneDefault = true;
        else if (et === "purchase") hasZonePurchase = true;
      }
    }

    return {
      hasAnalyzed, hasNotAnalyzed,
      hasLineTraffic, hasLineVisit, hasLineDefault,
      hasZoneDefault, hasZonePurchase, hasNoEventSource,
    };
  }, [camerasOnFloorPlan, configByCameraId]);

  // 도면 자동 선택 — 진입/버전 변경 시 모든 도면을 기본 선택 (4-5-1/4-5-2)
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    hasAutoSelectedRef.current = false;
    setSelectedFloorPlanIds(new Set());
  }, [versionId]);

  useEffect(() => {
    if (floorPlans.length > 0 && !hasAutoSelectedRef.current) {
      setSelectedFloorPlanIds(new Set(floorPlans.map((fp) => fp.id)));
      hasAutoSelectedRef.current = true;
    }
  }, [floorPlans]);

  // F14: 유저가 의도적으로 카메라를 선택 해제한 경우 auto-select 가 되돌리지 않도록 가드
  const intentionalDeselectRef = useRef(false);

  // 첫 카메라 자동 선택 — 현재 선택된 카메라가 필터된 목록에 없으면 첫 번째 카메라 선택
  useEffect(() => {
    // (F17) 빈 도면을 의도적으로 보고 있어 무선택으로 둔 경우 1회 건너뜀
    if (skipAutoSelectRef.current) {
      skipAutoSelectRef.current = false;
      return;
    }
    // F14: 유저가 의도적으로 선택 해제했으면 auto-select 건너뜀
    if (selectedCameraId === null && intentionalDeselectRef.current) return;
    if (filteredCamerasOnFloorPlan.length === 0) return;
    const currentExists = selectedCameraId !== null && filteredCamerasOnFloorPlan.some((c) => c.id === selectedCameraId);
    if (!currentExists) {
      setSelectedCameraId(filteredCamerasOnFloorPlan[0].id);
    }
  }, [filteredCamerasOnFloorPlan, selectedCameraId]);

  const handleCameraClick = useCallback((camera: Camera) => {
    setSelectedCameraId((prev) => {
      const deselecting = prev === camera.id;
      intentionalDeselectRef.current = deselecting;
      return deselecting ? null : camera.id;
    });
    setDrawingRequest(null);
    setSelectedEventSource(null);
    setSelectedMemoId(null);
    setMemoPlacingMode(null);
  }, []);

  // (F17) 도면 뷰 셀렉트로 도면 변경 시, 그 도면의 첫 카메라 자동 선택
  // → 보고 있는 도면과 선택 카메라(좌측 정보)의 비대칭 방지.
  // 빈 도면을 골라 무선택으로 둘 때, 아래 "첫 카메라 자동 선택" effect 가 다시 채우지 않도록 1회 가드.
  const skipAutoSelectRef = useRef(false);
  const handleViewerFloorPlanChange = useCallback((floorPlanId: number) => {
    intentionalDeselectRef.current = false; // 도면 전환 시 의도적 해제 상태 리셋
    setViewerFloorPlanId(floorPlanId);
    // (RV2) 그 도면의 첫 '보이는'(필터 통과) 카메라를 선택 — 필터된 목록(이미 표시번호 정렬됨) 기준이라
    // 자동선택 effect 의 검증(filteredCamerasOnFloorPlan.some)을 통과해 다른 도면으로 튀지 않음.
    const onPlan = filteredCamerasOnFloorPlan.filter((c) => c.floorPlanId === floorPlanId);
    // 보이는 카메라가 있으면 첫 카메라 선택, 없으면 카메라 선택 해제 (도면 선택은 유지 — 빈 도면 그대로 표시)
    if (onPlan.length > 0) {
      setSelectedCameraId(onPlan[0].id);
    } else {
      // (RL1) 실제로 해제될 때(선택이 있었을 때)만 가드 — 이미 무선택이면 setState 가 no-op 이라
      // 가드가 다음 effect 에 소비되지 않고 남는 누수 방지.
      if (selectedCameraId !== null) skipAutoSelectRef.current = true;
      setSelectedCameraId(null);
    }
    setSelectedEventSource(null);
    setSelectedMemoId(null);
    // (RL2) handleCameraClick 과 동일하게 드로잉/메모 배치 상태도 정리 — stale drawingRequest 방지
    setDrawingRequest(null);
    setMemoPlacingMode(null);
  }, [filteredCamerasOnFloorPlan, selectedCameraId]);

  const selectedCamera = cameras.find((c) => c.id === selectedCameraId);
  // (F17) 카메라 선택 시 뷰어 도면 자동 추종 — 수동으로 뷰 셀렉트를 바꿔도, 새 카메라 선택하면 다시 그 도면으로.
  useEffect(() => {
    if (selectedCamera?.floorPlanId != null) setViewerFloorPlanId(selectedCamera.floorPlanId);
  }, [selectedCamera?.floorPlanId]);
  // 뷰어에 띄울 도면 — 뷰 셀렉트(viewerFloorPlanId) 우선, 없으면 선택 카메라 도면, 그것도 없으면 첫 도면.
  // (매핑 없는 카메라 클릭 시 도면 영역이 통째로 사라지는 회귀 방지 — PO QA i 후속)
  const floorPlanForViewer =
    floorPlans.find((fp) => fp.id === viewerFloorPlanId)
    ?? floorPlans.find((fp) => fp.id === selectedCamera?.floorPlanId)
    ?? floorPlans[0];

  // 아이콘/라벨 크기는 "보기 설정"이라 이 페이지의 저장·제출 흐름과 분리 — 슬라이더 확정 시 바로 저장.
  // (그 흐름은 QA 로 잡아둔 곳이라 건드리지 않는다)
  const updateFloorPlanScale = useUpdateFloorPlan(branchUid!, versionId);
  const handleViewerScaleCommit = useCallback(
    (params: { iconSize?: number; labelSize?: number }) => {
      if (!floorPlanForViewer) return;
      updateFloorPlanScale.mutate({ floorPlanId: floorPlanForViewer.id, ...params });
    },
    [floorPlanForViewer?.id, updateFloorPlanScale],
  );

  // 분석 초안에선 도면 메모는 read-only — 서버 데이터 그대로 표시
  const visibleFloorPlanDiagramsForCurrentFloor = useMemo(
    () => (floorPlanForViewer ? floorPlanMemoDiagramsByFp[floorPlanForViewer.id] : undefined),
    [floorPlanForViewer, floorPlanMemoDiagramsByFp],
  );
  const visibleFloorPlanTextsForCurrentFloor = useMemo(
    () => (floorPlanForViewer ? floorPlanMemoTextsByFp[floorPlanForViewer.id] : undefined),
    [floorPlanForViewer, floorPlanMemoTextsByFp],
  );

  // 4-1-1: 도면 함께 보기에 매핑된 모든 카메라 표시 (현재 보고 있는 도면의 카메라들)
  const camerasOnFloorPlanForViewer = useMemo(
    () =>
      floorPlanForViewer
        ? cameras.filter((c) => c.floorPlanId === floorPlanForViewer.id)
        : [],
    [cameras, floorPlanForViewer],
  );

  // PO QA 8차 bf: 도면에는 선택 카메라뿐 아니라 같은 도면의 "다른 카메라" 이벤트소스도 보여줘야 함
  // (사용자가 다른 카메라의 배치를 고려하며 화각/이벤트소스를 설정 — 레거시 동작). 참조용이라 편집은 불가.
  const referenceCountingLines = useMemo(() => {
    // F14: 카메라 미선택 시 모든 카메라의 이벤트소스를 참조로 표시 (라벨 없이 전체 오버뷰)
    if (!selectedCameraId) {
      return camerasOnFloorPlanForViewer.flatMap((c) => configByCameraId.get(c.id)?.countingLines ?? []);
    }
    return camerasOnFloorPlanForViewer
      .filter((c) => c.id !== selectedCameraId)
      .flatMap((c) => configByCameraId.get(c.id)?.countingLines ?? []);
  }, [camerasOnFloorPlanForViewer, selectedCameraId, configByCameraId]);
  const referenceIntervalZones = useMemo(() => {
    if (!selectedCameraId) {
      return camerasOnFloorPlanForViewer.flatMap((c) => configByCameraId.get(c.id)?.intervalZones ?? []);
    }
    return camerasOnFloorPlanForViewer
      .filter((c) => c.id !== selectedCameraId)
      .flatMap((c) => configByCameraId.get(c.id)?.intervalZones ?? []);
  }, [camerasOnFloorPlanForViewer, selectedCameraId, configByCameraId]);

  // 카메라별 표시 이름: localConfigs의 analysisNumber 우선, 없으면 서버 데이터 → N넘버링 → defaultCamId
  const getDisplayName = useCallback(
    (camera: Camera) => {
      const cfg = configByCameraId.get(camera.id);
      if (cfg?.analysisNumber != null) return cfg.analysisNumber;
      if (camera.analysisNumber != null) return camera.analysisNumber;
      return nonAnalysisNumberMap.get(camera.id) ?? camera.defaultCamId;
    },
    [configByCameraId, nonAnalysisNumberMap],
  );

  const selectedDisplayName = selectedCamera ? getDisplayName(selectedCamera) : "";
  const isAnalyzing = (() => {
    if (!selectedCamera) return false;
    // selectedDisplayName이 이미 cfg → camera → nonAnalysisNumberMap 순으로 resolve됨
    // 빈 문자열이면 편집 중이므로 기존 상태 유지
    if (selectedDisplayName === "") {
      const cfg = configByCameraId.get(selectedCamera.id);
      // cfg가 없거나 빈 문자열이면 서버 데이터 기준
      if (!cfg || cfg.analysisNumber === "" || cfg.analysisNumber === null) return isCameraAnalyzed(selectedCamera.analysisNumber);
      return !cfg.analysisNumber.startsWith("N");
    }
    return !selectedDisplayName.startsWith("N");
  })();

  // 다른 카메라들의 번호 세트 (자기 자신 제외) — 중복 체크용
  // isDuplicateNumber: 현재 선택된 카메라의 번호가 다른 카메라와 중복인지
  const usedAnalysisNumbers = useMemo(() => {
    const set = new Set<string>();
    for (const cam of cameras) {
      if (cam.id === selectedCameraId) continue;
      set.add(getDisplayName(cam));
    }
    return set;
  }, [cameras, selectedCameraId, getDisplayName]);

  const isDuplicateNumber = !!selectedDisplayName && usedAnalysisNumbers.has(selectedDisplayName);
  const isEmptyNumber = !!selectedCamera && selectedDisplayName.replace(/^N/, "") === "";

  // 전체 카메라 중 중복되는 분석 번호 집합
  const duplicateDisplayNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const cam of cameras) {
      const name = getDisplayName(cam);
      if (name) {
        if (seen.has(name)) dupes.add(name);
        seen.add(name);
      }
    }
    return dupes;
  }, [cameras, getDisplayName]);

  // 전체 카메라에 걸쳐 중복되는 key 집합
  const globalDuplicateKeys = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    // suffix 가 빈 key(prefix만 남은 상태)는 중복이 아니라 '미입력' — 중복 검사에서 제외.
    // (안 그러면 여러 라인/구역을 비웠을 때 prefix 가 같아 가짜 'Key 중복' 으로 잡힘)
    const isEmptySuffix = (key: string) => {
      const parts = key.split("_");
      const suffix = parts.length > 2 ? parts.slice(2).join("_") : (parts[parts.length - 1] ?? "");
      return suffix.trim() === "";
    };
    for (const cfg of localConfigs) {
      for (const l of cfg.countingLines) {
        if (isEmptySuffix(l.key)) continue;
        if (seen.has(l.key)) dupes.add(l.key);
        seen.add(l.key);
      }
      for (const z of cfg.intervalZones) {
        if (isEmptySuffix(z.key)) continue;
        if (seen.has(z.key)) dupes.add(z.key);
        seen.add(z.key);
      }
    }
    return dupes;
  }, [localConfigs]);

  // 카메라 간 이벤트 소스 이름 중복 집합 (경고용)
  const crossCameraDuplicateNames = useMemo(() => {
    const nameToCount = new Map<string, number>();
    for (const cfg of localConfigs) {
      const perCamera = new Set<string>();
      for (const item of [...cfg.countingLines, ...cfg.intervalZones]) {
        const n = item.analysisConfig?.name ?? "";
        if (n !== "") perCamera.add(n);
      }
      for (const n of perCamera) {
        nameToCount.set(n, (nameToCount.get(n) ?? 0) + 1);
      }
    }
    const dupes = new Set<string>();
    for (const [n, c] of nameToCount) {
      if (c > 1) dupes.add(n);
    }
    return dupes;
  }, [localConfigs]);

  // 메모는 즉시 저장이고, 저장이 끝나기 전 화면 반영은 mutation 의 낙관적 업데이트가
  // 쿼리 캐시에 직접 해준다. 그래서 여기서 따로 합산할 미저장분이 없다.
  const effectiveMemos = cameraMemos;

  // 전체 유효성 검사: 오류/경고 사유 목록 (구체적인 값 포함)
  type ValidationItem = { message: string; values: string[] };
  type ValidationResult = { errors: ValidationItem[]; warnings: ValidationItem[] };

  const validationResult = useMemo<ValidationResult>(() => {
    const errors: ValidationItem[] = [];
    const warnings: ValidationItem[] = [];

    // 오류 안내 시 "어떤 도면의 어떤 카메라"인지 식별할 수 있도록 컨텍스트 라벨 생성.
    // 도면이 연결돼 있으면 `[도면명 > 카메라명]`, 없으면 `[카메라명]`.
    const floorPlanNameById = new Map<number, string>(floorPlans.map((fp) => [fp.id, fp.name]));
    const camCtx = (camId: number) => {
      const cam = cameras.find((c) => c.id === camId);
      const camName = cam ? getDisplayName(cam) : String(camId);
      const fpName = cam?.floorPlanId != null ? floorPlanNameById.get(cam.floorPlanId) : undefined;
      return fpName ? `[${fpName} > ${camName}]` : `[${camName}]`;
    };

    // 오류: 분석 번호 중복
    if (duplicateDisplayNames.size > 0) {
      errors.push({ message: "tooltip.duplicateNumber", values: [...duplicateDisplayNames] });
    }

    // 오류: 분석 번호 미입력
    const emptyNumberCameras: string[] = [];
    for (const cam of cameras) {
      const name = getDisplayName(cam);
      if (name.replace(/^N/, "") === "") {
        // 번호가 비어있으므로 서버 원본 번호 또는 N번호로 안내. 어떤 도면인지도 함께 표기
        const fallback = cam.analysisNumber ?? nonAnalysisNumberMap.get(cam.id) ?? cam.defaultCamId;
        const fpName = cam.floorPlanId != null ? floorPlanNameById.get(cam.floorPlanId) : undefined;
        emptyNumberCameras.push(fpName ? `[${fpName}] ${fallback}` : fallback);
      }
    }
    if (emptyNumberCameras.length > 0) {
      errors.push({ message: "tooltip.emptyNumber", values: emptyNumberCameras });
    }

    // 오류: Key 미입력
    const emptyKeyItems: string[] = [];
    for (const cfg of localConfigs) {
      const ctx = camCtx(cfg.cameraId);
      for (const item of [...cfg.countingLines, ...cfg.intervalZones]) {
        const parts = item.key.split("_");
        const suffix = parts.length > 2 ? parts.slice(2).join("_") : parts.at(-1) ?? "";
        if (suffix.trim() === "") {
          const esName = item.analysisConfig?.name || item.key;
          emptyKeyItems.push(`${ctx} ${esName}`);
        }
      }
    }
    if (emptyKeyItems.length > 0) {
      errors.push({ message: "tooltip.emptyKey", values: emptyKeyItems });
    }

    // 오류: Key 중복 (전체)
    if (globalDuplicateKeys.size > 0) {
      const dupKeyValues: string[] = [];
      for (const key of globalDuplicateKeys) {
        const owners: string[] = [];
        for (const cfg of localConfigs) {
          const has = [...cfg.countingLines, ...cfg.intervalZones].some((item) => item.key === key);
          if (has) {
            const cam = cameras.find((c) => c.id === cfg.cameraId);
            owners.push(cam ? getDisplayName(cam) : String(cfg.cameraId));
          }
        }
        const suffix = key.split("_").slice(2).join("_") || key;
        dupKeyValues.push(`${suffix} (${owners.join(", ")})`);
      }
      errors.push({ message: "tooltip.duplicateKey", values: dupKeyValues });
    }

    // 오류: Key 포맷 위반 — 영문 대소문자/숫자/하이픈만 허용 (PO QA 2차 m, 레거시 정규식 동일).
    // 입력은 IME 보호를 위해 onChange 단계에서 차단하지 않고 저장 시점에 검증.
    const KEY_SUFFIX_FORMAT = /^[a-zA-Z0-9-]*$/;
    const invalidKeyItems: string[] = [];
    for (const cfg of localConfigs) {
      const ctx = camCtx(cfg.cameraId);
      for (const item of [...cfg.countingLines, ...cfg.intervalZones]) {
        const parts = item.key.split("_");
        const suffix = parts.length > 2 ? parts.slice(2).join("_") : parts.at(-1) ?? "";
        if (suffix !== "" && !KEY_SUFFIX_FORMAT.test(suffix)) {
          const esName = item.analysisConfig?.name || item.key;
          invalidKeyItems.push(`${ctx} ${esName}`);
        }
      }
    }
    if (invalidKeyItems.length > 0) {
      errors.push({ message: "tooltip.invalidKeyFormat", values: invalidKeyItems });
    }

    // 오류: 같은 카메라 내 이벤트 소스 이름 중복
    const intraDuplicateNames: string[] = [];
    for (const cfg of localConfigs) {
      const names = [
        ...cfg.countingLines.map((l) => l.analysisConfig?.name ?? ""),
        ...cfg.intervalZones.map((z) => z.analysisConfig?.name ?? ""),
      ].filter((n) => n !== "");
      const seen = new Set<string>();
      for (const n of names) {
        if (seen.has(n) && !intraDuplicateNames.includes(n)) intraDuplicateNames.push(n);
        seen.add(n);
      }
    }
    if (intraDuplicateNames.length > 0) {
      errors.push({ message: "tooltip.duplicateName", values: intraDuplicateNames });
    }

    // 오류: 이벤트 소스 이름 미입력
    const emptyNameItems: string[] = [];
    for (const cfg of localConfigs) {
      const ctx = camCtx(cfg.cameraId);
      for (const item of [...cfg.countingLines, ...cfg.intervalZones]) {
        if ((item.analysisConfig?.name ?? "").trim() === "") {
          // 이름이 비어 식별이 어려우므로 도면·카메라 컨텍스트 + key 로 안내
          emptyNameItems.push(`${ctx} ${item.key}`);
        }
      }
    }
    if (emptyNameItems.length > 0) {
      errors.push({ message: "tooltip.emptyName", values: emptyNameItems });
    }

    // 오류: 빈 카메라 메모
    const emptyMemoCameras: string[] = [];
    for (const memo of effectiveMemos) {
      if (memo.content.trim() === "") {
        const ctx = camCtx(memo.cameraId);
        if (!emptyMemoCameras.includes(ctx)) emptyMemoCameras.push(ctx);
      }
    }
    if (emptyMemoCameras.length > 0) {
      errors.push({ message: "tooltip.emptyMemo", values: emptyMemoCameras });
    }

    // 경고: 카메라 간 이벤트 소스 이름 중복
    if (crossCameraDuplicateNames.size > 0) {
      warnings.push({ message: "tooltip.crossCameraDuplicateName", values: [...crossCameraDuplicateNames] });
    }

    return { errors, warnings };
  }, [duplicateDisplayNames, cameras, getDisplayName, localConfigs, globalDuplicateKeys, crossCameraDuplicateNames, nonAnalysisNumberMap, effectiveMemos, floorPlans]);

  const hasAnyValidationError = validationResult.errors.length > 0;

  // 특정 prefix 그룹에서 중복되지 않는 다음 번호 계산
  const getNextAvailableNumber = useCallback(
    (prefix: string) => {
      let n = 1;
      while (usedAnalysisNumbers.has(`${prefix}${n}`)) n++;
      return n.toString();
    },
    [usedAnalysisNumbers],
  );

  // 분석 대상 토글: ON → 숫자만(N 제거), OFF → N prefix 추가
  const handleAnalysisToggle = useCallback(
    (checked: boolean) => {
      if (!selectedCameraId) return;
      pushUndo();

      const currentNum = selectedDisplayName.replace(/^N/, "") || "1";
      let newNumber: string;

      if (checked) {
        // N 제거 → 분석 번호 그룹으로 이동. 중복이면 새 번호 부여
        newNumber = usedAnalysisNumbers.has(currentNum)
          ? getNextAvailableNumber("")
          : currentNum;
      } else {
        // N prefix 추가 → 미분석 그룹으로 이동. 중복이면 새 번호 부여
        const candidate = `N${currentNum}`;
        newNumber = usedAnalysisNumbers.has(candidate)
          ? `N${getNextAvailableNumber("N")}`
          : candidate;
      }

      setLocalConfigs((prev) => {
        const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
        if (exists) {
          return prev.map((cfg) =>
            cfg.cameraId === selectedCameraId
              ? { ...cfg, analysisNumber: newNumber }
              : cfg,
          );
        }
        return [
          ...prev,
          {
            cameraId: selectedCameraId,
            pseudoCameraId: selectedCameraId,
            analysisNumber: newNumber,
            floorPlanId: null,
            countingLines: [],
            intervalZones: [],
          },
        ];
      });
    },
    [selectedCameraId, selectedDisplayName, usedAnalysisNumbers, getNextAvailableNumber],
  );

  // 분석 번호 직접 수정 — legacy 형식 허용: "1", "12", "b", "b1", "1-2", "b12-345"
  // 중간 입력 상태("1-", "b1-")도 허용; 중복 시 에러 표시 (입력은 허용)
  const handleAnalysisNumberChange = useCallback(
    (value: string) => {
      if (!selectedCameraId) return;
      if (
        value !== "" &&
        value !== "b" &&
        !/^b?[1-9]\d*(?:-\d*)?$/.test(value)
      ) {
        return;
      }
      pushUndo();

      const prefix = isAnalyzing ? "" : "N";
      const candidate = `${prefix}${value}`;

      setLocalConfigs((prev) => {
        const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
        if (exists) {
          return prev.map((cfg) =>
            cfg.cameraId === selectedCameraId
              ? { ...cfg, analysisNumber: candidate }
              : cfg,
          );
        }
        return [
          ...prev,
          {
            cameraId: selectedCameraId,
            pseudoCameraId: selectedCameraId,
            analysisNumber: candidate,
            floorPlanId: null,
            countingLines: [],
            intervalZones: [],
          },
        ];
      });
    },
    [selectedCameraId, isAnalyzing],
  );

  // --- 라인/구역 추가: 드로잉 요청 → 사용자가 클릭한 캔버스(스냅샷 or 도면)에서 그리기 시작 (4-3-2) ---
  // PO QA 4차 ah: UI disabled 우회 방어 — 액션 핸들러에서도 재확인
  // F13: 스냅샷 없어도 도면 뷰가 활성이면 도면 위 이벤트소스 추가 허용
  const handleAddLine = useCallback(
    (eventType: CountingLineEventType = "default") => {
      if (!selectedCameraId) return;
      const camera = cameras.find((c) => c.id === selectedCameraId);
      if (!camera) return;
      if (!hasRealSnapshot(camera) && !showFloorPlan) return;
      setDrawingRequest({ type: "line", eventType });
    },
    [selectedCameraId, cameras, showFloorPlan],
  );

  const handleAddZone = useCallback(
    (eventType: IntervalZoneEventType = "default") => {
      if (!selectedCameraId) return;
      const camera = cameras.find((c) => c.id === selectedCameraId);
      if (!camera) return;
      if (!hasRealSnapshot(camera) && !showFloorPlan) return;
      setDrawingRequest({ type: "zone", eventType });
    },
    [selectedCameraId, cameras, showFloorPlan],
  );

  const handleDrawingComplete = useCallback(
    (type: "line" | "zone", eventType: string, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setDrawingRequest(null);

      if (type === "line") {
        const lineEventType = eventType as CountingLineEventType;
        const allLines = localConfigs.flatMap((c) => c.countingLines);

        const prefix = `line_${lineEventType}_`;
        const nextCount =
          allLines.reduce((acc, l) => {
            if (l.key.startsWith(prefix)) {
              const num = parseInt(l.key.slice(prefix.length));
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;

        const eventTypeNames: Record<CountingLineEventType, string> = {
          default: t("defaultName.lineDefault"),
          traffic: t("defaultName.lineTraffic"),
          visit: t("defaultName.lineVisit"),
        };
        const namePrefix = eventTypeNames[lineEventType];
        const nameRegex = new RegExp(`^${namePrefix} \\d+$`);
        const nextNameCount =
          allLines.reduce((acc, l) => {
            if (l.analysisConfig && nameRegex.test(l.analysisConfig.name)) {
              const num = parseInt(l.analysisConfig.name.split(" ").at(-1)!);
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;

        const newLine: CountingLine = {
          id: getNextLocalId(),
          key: `${prefix}${nextCount}`,
          cameraId: selectedCameraId,
          analysisConfig: {
            name: `${namePrefix} ${nextNameCount}`,
            eventType: lineEventType,
            coordinates,
          },
          floorPlanConfig: null,
        };

        setLocalConfigs((prev) => {
          const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
          if (exists) {
            return prev.map((cfg) =>
              cfg.cameraId === selectedCameraId
                ? { ...cfg, countingLines: [...cfg.countingLines, newLine] }
                : cfg,
            );
          }
          return [
            ...prev,
            {
              cameraId: selectedCameraId,
              pseudoCameraId: selectedCameraId,
              analysisNumber: null,
              floorPlanId: null,
              countingLines: [newLine],
              intervalZones: [],
            },
          ];
        });
      } else {
        const zoneEventType = eventType as IntervalZoneEventType;
        const allZones = localConfigs.flatMap((c) => c.intervalZones);

        const prefix = `zone_${zoneEventType}_`;
        const nextCount =
          allZones.reduce((acc, z) => {
            if (z.key.startsWith(prefix)) {
              const num = parseInt(z.key.slice(prefix.length));
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;

        const eventTypeNames: Record<IntervalZoneEventType, string> = {
          default: t("defaultName.zoneDefault"),
          purchase: t("defaultName.zonePurchase"),
        };
        const namePrefix = eventTypeNames[zoneEventType];
        const nameRegex = new RegExp(`^${namePrefix} \\d+$`);
        const nextNameCount =
          allZones.reduce((acc, z) => {
            if (z.analysisConfig && nameRegex.test(z.analysisConfig.name)) {
              const num = parseInt(z.analysisConfig.name.split(" ").at(-1)!);
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;

        const newZone: IntervalZone = {
          id: getNextLocalId(),
          key: `${prefix}${nextCount}`,
          cameraId: selectedCameraId,
          analysisConfig: {
            name: `${namePrefix} ${nextNameCount}`,
            eventType: zoneEventType,
            coordinates,
          },
          floorPlanConfig: null,
        };

        setLocalConfigs((prev) => {
          const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
          if (exists) {
            return prev.map((cfg) =>
              cfg.cameraId === selectedCameraId
                ? { ...cfg, intervalZones: [...cfg.intervalZones, newZone] }
                : cfg,
            );
          }
          return [
            ...prev,
            {
              cameraId: selectedCameraId,
              pseudoCameraId: selectedCameraId,
              analysisNumber: null,
              floorPlanId: null,
              countingLines: [],
              intervalZones: [newZone],
            },
          ];
        });
      }
    },
    [selectedCameraId, localConfigs],
  );

  // 캔버스에서 라인/구역 좌표 변경
  const handleLineCoordinatesChange = useCallback(
    (lineId: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                countingLines: cfg.countingLines.map((l) =>
                  l.id === lineId && l.analysisConfig
                    ? { ...l, analysisConfig: { ...l.analysisConfig, coordinates } }
                    : l,
                ),
              }
            : cfg,
        ),
      );
    },
    [selectedCameraId],
  );

  const handleZoneCoordinatesChange = useCallback(
    (zoneId: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                intervalZones: cfg.intervalZones.map((z) =>
                  z.id === zoneId && z.analysisConfig
                    ? { ...z, analysisConfig: { ...z.analysisConfig, coordinates } }
                    : z,
                ),
              }
            : cfg,
        ),
      );
    },
    [selectedCameraId],
  );

  // 도면 위에서 라인/구역 그리기 완료
  // 4-3-2 역방향: 도면에만 있던 라인/존을 스냅샷에 그려서 analysisConfig.coordinates 채움
  const handleSnapshotDrawingTargetComplete = useCallback(
    (type: "line" | "zone", id: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                countingLines:
                  type === "line"
                    ? cfg.countingLines.map((l) =>
                        l.id === id
                          ? {
                              ...l,
                              analysisConfig: l.analysisConfig
                                ? { ...l.analysisConfig, coordinates }
                                : { name: "", eventType: "default" as const, coordinates },
                            }
                          : l,
                      )
                    : cfg.countingLines,
                intervalZones:
                  type === "zone"
                    ? cfg.intervalZones.map((z) =>
                        z.id === id
                          ? {
                              ...z,
                              analysisConfig: z.analysisConfig
                                ? { ...z.analysisConfig, coordinates }
                                : { name: "", eventType: "default" as const, coordinates },
                            }
                          : z,
                      )
                    : cfg.intervalZones,
              }
            : cfg,
        ),
      );
      setSnapshotDrawingTarget(null);
    },
    [selectedCameraId],
  );

  const handleFloorPlanDrawingComplete = useCallback(
    (type: "line" | "zone", id: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                countingLines:
                  type === "line"
                    ? cfg.countingLines.map((l) =>
                        l.id === id ? { ...l, floorPlanConfig: { coordinates } } : l,
                      )
                    : cfg.countingLines,
                intervalZones:
                  type === "zone"
                    ? cfg.intervalZones.map((z) =>
                        z.id === id ? { ...z, floorPlanConfig: { coordinates } } : z,
                      )
                    : cfg.intervalZones,
              }
            : cfg,
        ),
      );
      setFloorPlanDrawingTarget(null);
    },
    [selectedCameraId],
  );

  // 4-3-2: 도면에서 신규 라인/존 drawing 완료. analysisConfig 는 draft(이름만, coords 빈), floorPlanConfig 에 좌표 저장
  const handleFloorPlanDrawingRequestComplete = useCallback(
    (type: "line" | "zone", eventType: string, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setDrawingRequest(null);

      if (type === "line") {
        const lineEventType = eventType as CountingLineEventType;
        const allLines = localConfigs.flatMap((c) => c.countingLines);
        const prefix = `line_${lineEventType}_`;
        const nextCount =
          allLines.reduce((acc, l) => {
            if (l.key.startsWith(prefix)) {
              const num = parseInt(l.key.slice(prefix.length));
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;
        const eventTypeNames: Record<CountingLineEventType, string> = {
          default: t("defaultName.lineDefault"),
          traffic: t("defaultName.lineTraffic"),
          visit: t("defaultName.lineVisit"),
        };
        const namePrefix = eventTypeNames[lineEventType];
        const nameRegex = new RegExp(`^${namePrefix} \\d+$`);
        const nextNameCount =
          allLines.reduce((acc, l) => {
            if (l.analysisConfig && nameRegex.test(l.analysisConfig.name)) {
              const num = parseInt(l.analysisConfig.name.split(" ").at(-1)!);
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;
        const newLine: CountingLine = {
          id: getNextLocalId(),
          key: `${prefix}${nextCount}`,
          cameraId: selectedCameraId,
          analysisConfig: { name: `${namePrefix} ${nextNameCount}`, eventType: lineEventType, coordinates: [] },
          floorPlanConfig: { coordinates },
        };
        setLocalConfigs((prev) => {
          const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
          if (exists) {
            return prev.map((cfg) =>
              cfg.cameraId === selectedCameraId
                ? { ...cfg, countingLines: [...cfg.countingLines, newLine] }
                : cfg,
            );
          }
          return [
            ...prev,
            {
              cameraId: selectedCameraId,
              pseudoCameraId: selectedCameraId,
              analysisNumber: null,
              floorPlanId: null,
              countingLines: [newLine],
              intervalZones: [],
            },
          ];
        });
      } else {
        const zoneEventType = eventType as IntervalZoneEventType;
        const allZones = localConfigs.flatMap((c) => c.intervalZones);
        const prefix = `zone_${zoneEventType}_`;
        const nextCount =
          allZones.reduce((acc, z) => {
            if (z.key.startsWith(prefix)) {
              const num = parseInt(z.key.slice(prefix.length));
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;
        const eventTypeNames: Record<IntervalZoneEventType, string> = {
          default: t("defaultName.zoneDefault"),
          purchase: t("defaultName.zonePurchase"),
        };
        const namePrefix = eventTypeNames[zoneEventType];
        const nameRegex = new RegExp(`^${namePrefix} \\d+$`);
        const nextNameCount =
          allZones.reduce((acc, z) => {
            if (z.analysisConfig && nameRegex.test(z.analysisConfig.name)) {
              const num = parseInt(z.analysisConfig.name.split(" ").at(-1)!);
              return isNaN(num) ? acc : Math.max(acc, num);
            }
            return acc;
          }, 0) + 1;
        const newZone: IntervalZone = {
          id: getNextLocalId(),
          key: `${prefix}${nextCount}`,
          cameraId: selectedCameraId,
          analysisConfig: { name: `${namePrefix} ${nextNameCount}`, eventType: zoneEventType, coordinates: [] },
          floorPlanConfig: { coordinates },
        };
        setLocalConfigs((prev) => {
          const exists = prev.some((cfg) => cfg.cameraId === selectedCameraId);
          if (exists) {
            return prev.map((cfg) =>
              cfg.cameraId === selectedCameraId
                ? { ...cfg, intervalZones: [...cfg.intervalZones, newZone] }
                : cfg,
            );
          }
          return [
            ...prev,
            {
              cameraId: selectedCameraId,
              pseudoCameraId: selectedCameraId,
              analysisNumber: null,
              floorPlanId: null,
              countingLines: [],
              intervalZones: [newZone],
            },
          ];
        });
      }
    },
    [selectedCameraId, localConfigs, t, pushUndo],
  );

  // 도면 위에서 라인/구역 좌표 변경
  const handleFloorPlanLineCoordinatesChange = useCallback(
    (lineId: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                countingLines: cfg.countingLines.map((l) =>
                  l.id === lineId && l.floorPlanConfig
                    ? { ...l, floorPlanConfig: { ...l.floorPlanConfig, coordinates } }
                    : l,
                ),
              }
            : cfg,
        ),
      );
    },
    [selectedCameraId],
  );

  const handleFloorPlanZoneCoordinatesChange = useCallback(
    (zoneId: number, coordinates: { x: number; y: number }[]) => {
      if (!selectedCameraId) return;
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === selectedCameraId
            ? {
                ...cfg,
                intervalZones: cfg.intervalZones.map((z) =>
                  z.id === zoneId && z.floorPlanConfig
                    ? { ...z, floorPlanConfig: { ...z.floorPlanConfig, coordinates } }
                    : z,
                ),
              }
            : cfg,
        ),
      );
    },
    [selectedCameraId],
  );

  // --- 라인/구역 삭제 ---
  const requestDeleteLine = useCallback(
    (cameraId: number, lineId: number) => {
      const cfg = localConfigs.find((c) => c.cameraId === cameraId);
      const line = cfg?.countingLines.find((l) => l.id === lineId);
      if (!line) return;
      setDeleteTarget({
        type: "line",
        cameraId,
        id: lineId,
        key: line.key,
        // 스냅샷에 실제 좌표가 있을 때만 "스냅샷 삭제" 활성 — analysisConfig 객체만 남고 coords 가 비어있는
        // (이전에 스냅샷만 삭제했던) 케이스에서 체크박스가 잘못 활성화되던 버그 보강
        hasAnalysisConfig: !!line.analysisConfig && line.analysisConfig.coordinates.length > 0,
        hasFloorPlanConfig: line.floorPlanConfig !== null,
      });
    },
    [localConfigs],
  );

  const requestDeleteZone = useCallback(
    (cameraId: number, zoneId: number) => {
      const cfg = localConfigs.find((c) => c.cameraId === cameraId);
      const zone = cfg?.intervalZones.find((z) => z.id === zoneId);
      if (!zone) return;
      setDeleteTarget({
        type: "zone",
        cameraId,
        id: zoneId,
        key: zone.key,
        hasAnalysisConfig: !!zone.analysisConfig && zone.analysisConfig.coordinates.length > 0,
        hasFloorPlanConfig: zone.floorPlanConfig !== null,
      });
    },
    [localConfigs],
  );

  const handleConfirmDelete = useCallback(
    (target: DeleteLineZoneTarget, deleteSnapshot: boolean, deleteFloorPlan: boolean) => {
      pushUndo();
      // 스냅샷만 삭제 시: analysisConfig 의 coordinates 만 비우고 name/eventType 유지 (재배치 시 이름 보존)
      // 도면도 같이 삭제(둘 다)하는 경우 analysisConfig 도 null 로 초기화 → 라인이 완전히 제거됨
      // 라인/구역이 카드에서 사라져야 하는 조건: 스냅샷 좌표 0개 + floorPlanConfig null.
      // analysisConfig 객체가 남아있어도 좌표가 비어있고 도면도 없으면 의미 없음 → 완전 제거.
      const hasUsefulData = (analysisConfig: { coordinates: { x: number; y: number }[] } | null, floorPlanConfig: unknown | null) => {
        const hasAnalysis = !!analysisConfig && analysisConfig.coordinates.length > 0;
        return hasAnalysis || floorPlanConfig != null;
      };
      if (target.type === "line") {
        setLocalConfigs((prev) =>
          prev.map((cfg) => {
            if (cfg.cameraId !== target.cameraId) return cfg;
            return {
              ...cfg,
              countingLines: cfg.countingLines
                .map((l) => {
                  if (l.id !== target.id) return l;
                  const nextFloorPlan = deleteFloorPlan ? null : l.floorPlanConfig;
                  const nextAnalysis = deleteSnapshot
                    ? (l.analysisConfig
                        ? (deleteFloorPlan ? null : { ...l.analysisConfig, coordinates: [] })
                        : null)
                    : l.analysisConfig;
                  return { ...l, analysisConfig: nextAnalysis, floorPlanConfig: nextFloorPlan };
                })
                .filter((l) => hasUsefulData(l.analysisConfig, l.floorPlanConfig)),
            };
          }),
        );
      } else {
        setLocalConfigs((prev) =>
          prev.map((cfg) => {
            if (cfg.cameraId !== target.cameraId) return cfg;
            return {
              ...cfg,
              intervalZones: cfg.intervalZones
                .map((z) => {
                  if (z.id !== target.id) return z;
                  const nextFloorPlan = deleteFloorPlan ? null : z.floorPlanConfig;
                  const nextAnalysis = deleteSnapshot
                    ? (z.analysisConfig
                        ? (deleteFloorPlan ? null : { ...z.analysisConfig, coordinates: [] })
                        : null)
                    : z.analysisConfig;
                  return { ...z, analysisConfig: nextAnalysis, floorPlanConfig: nextFloorPlan };
                })
                .filter((z) => hasUsefulData(z.analysisConfig, z.floorPlanConfig)),
            };
          }),
        );
      }
      // 스냅샷만 삭제 + 도면 잔존 → 즉시 스냅샷 재그리기 모드 진입
      // (카드를 다시 클릭해야 그릴 수 있는 불편함 제거 — QA aa)
      if (deleteSnapshot && !deleteFloorPlan) {
        const name = target.type === "line"
          ? localConfigs
              .find((c) => c.cameraId === target.cameraId)
              ?.countingLines.find((l) => l.id === target.id)?.analysisConfig?.name ?? ""
          : localConfigs
              .find((c) => c.cameraId === target.cameraId)
              ?.intervalZones.find((z) => z.id === target.id)?.analysisConfig?.name ?? "";
        setSelectedEventSource({ type: target.type, id: target.id });
        setViewMode((m) => (m === "floorPlan" ? "both" : m));
        setSnapshotDrawingTarget({ type: target.type, id: target.id, name });
        setFloorPlanDrawingTarget(null);
      } else {
        setSelectedEventSource(null);
      }
      setDeleteTarget(null);
    },
    [localConfigs],
  );

  // --- 라인/구역 이름/키 수정 ---
  const handleUpdateLineName = useCallback(
    (cameraId: number, lineId: number, name: string) => {
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === cameraId
            ? {
                ...cfg,
                countingLines: cfg.countingLines.map((l) => {
                  if (l.id !== lineId) return l;
                  // 스냅샷 미배치 상태(analysisConfig=null) 에서도 이름 입력 가능 — analysisConfig 가 없으면 기본값으로 생성
                  if (!l.analysisConfig) {
                    return { ...l, analysisConfig: { name, eventType: "default" as const, coordinates: [] } };
                  }
                  return { ...l, analysisConfig: { ...l.analysisConfig, name } };
                }),
              }
            : cfg,
        ),
      );
    },
    [],
  );

  const handleUpdateLineKey = useCallback(
    (cameraId: number, lineId: number, keySuffix: string) => {
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === cameraId
            ? {
                ...cfg,
                countingLines: cfg.countingLines.map((l) => {
                  if (l.id !== lineId) return l;
                  const prefix = l.key.includes("_") ? l.key.substring(0, l.key.lastIndexOf("_") + 1) : "line_default_";
                  return { ...l, key: prefix + keySuffix };
                }),
              }
            : cfg,
        ),
      );
    },
    [],
  );

  const handleUpdateZoneName = useCallback(
    (cameraId: number, zoneId: number, name: string) => {
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === cameraId
            ? {
                ...cfg,
                intervalZones: cfg.intervalZones.map((z) => {
                  if (z.id !== zoneId) return z;
                  if (!z.analysisConfig) {
                    return { ...z, analysisConfig: { name, eventType: "default" as const, coordinates: [] } };
                  }
                  return { ...z, analysisConfig: { ...z.analysisConfig, name } };
                }),
              }
            : cfg,
        ),
      );
    },
    [],
  );

  const handleUpdateZoneKey = useCallback(
    (cameraId: number, zoneId: number, keySuffix: string) => {
      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) =>
          cfg.cameraId === cameraId
            ? {
                ...cfg,
                intervalZones: cfg.intervalZones.map((z) => {
                  if (z.id !== zoneId) return z;
                  const prefix = z.key.includes("_") ? z.key.substring(0, z.key.lastIndexOf("_") + 1) : "zone_default_";
                  return { ...z, key: prefix + keySuffix };
                }),
              }
            : cfg,
        ),
      );
    },
    [],
  );

  // --- Key 자동 입력 (도면 전체 카메라 일괄, 빈 칸만 채움) ---
  const generateKeysMut = useGenerateKeys(branchUid ?? "");
  const [isGeneratingKeys, setIsGeneratingKeys] = useState(false);

  const handleGenerateKeys = useCallback(async () => {
    const suffixOf = (key: string) => {
      const parts = key.split("_");
      return parts.length > 2 ? parts.slice(2).join("_") : (parts[parts.length - 1] ?? "");
    };

    // 현재 선택된 도면의 모든 카메라 중, 이름이 있고 Key suffix 가 빈 라인/구역만 수집.
    // floorPlanName 은 Key 규칙 고객사의 {floor} 출처 (도면마다 다를 수 있어 항목별로 담음).
    const floorPlanNameById = new Map<number, string>(floorPlans.map((fp) => [fp.id, fp.name]));
    const entries: { reqId: string; name: string; floorPlanName: string }[] = [];
    for (const cfg of localConfigs) {
      if (cfg.floorPlanId == null || !selectedFloorPlanIds.has(cfg.floorPlanId)) continue;
      const floorPlanName = floorPlanNameById.get(cfg.floorPlanId) ?? "";
      for (const line of cfg.countingLines) {
        const name = line.analysisConfig?.name?.trim() ?? "";
        if (name && suffixOf(line.key).trim() === "") entries.push({ reqId: `line-${line.id}`, name, floorPlanName });
      }
      for (const zone of cfg.intervalZones) {
        const name = zone.analysisConfig?.name?.trim() ?? "";
        if (name && suffixOf(zone.key).trim() === "") entries.push({ reqId: `zone-${zone.id}`, name, floorPlanName });
      }
    }

    if (entries.length === 0) {
      showToast({ type: "info", title: t("toast.generateKeysEmpty.title"), content: t("toast.generateKeysEmpty.content") });
      return;
    }

    setIsGeneratingKeys(true);
    try {
      const { results } = await generateKeysMut.mutateAsync(
        entries.map((e) => ({ id: e.reqId, name: e.name, floor_plan_name: e.floorPlanName })),
      );
      const suffixById = new Map<string, string>();
      for (const r of results) {
        if (r.key_suffix) suffixById.set(String(r.id), r.key_suffix);
      }

      const appliedCount = entries.filter((e) => suffixById.get(e.reqId)).length;
      if (appliedCount === 0) {
        showToast({ type: "info", title: t("toast.generateKeysEmpty.title"), content: t("toast.generateKeysEmpty.content") });
        return;
      }

      pushUndo();
      setLocalConfigs((prev) =>
        prev.map((cfg) => {
          if (cfg.floorPlanId == null || !selectedFloorPlanIds.has(cfg.floorPlanId)) return cfg;
          let changed = false;
          const countingLines = cfg.countingLines.map((l) => {
            const suffix = suffixById.get(`line-${l.id}`);
            if (!suffix || suffixOf(l.key).trim() !== "") return l; // 빈 칸만 채움
            changed = true;
            const prefix = l.key.includes("_") ? l.key.substring(0, l.key.lastIndexOf("_") + 1) : "line_default_";
            return { ...l, key: prefix + suffix };
          });
          const intervalZones = cfg.intervalZones.map((z) => {
            const suffix = suffixById.get(`zone-${z.id}`);
            if (!suffix || suffixOf(z.key).trim() !== "") return z;
            changed = true;
            const prefix = z.key.includes("_") ? z.key.substring(0, z.key.lastIndexOf("_") + 1) : "zone_default_";
            return { ...z, key: prefix + suffix };
          });
          return changed ? { ...cfg, countingLines, intervalZones } : cfg;
        }),
      );
      showToast({ type: "success", title: t("toast.generateKeysSuccess.title"), content: t("toast.generateKeysSuccess.content", { count: appliedCount }) });
    } catch {
      showToast({ type: "error", title: t("toast.generateKeysError.title"), content: t("toast.generateKeysError.content") });
    } finally {
      setIsGeneratingKeys(false);
    }
  }, [localConfigs, selectedFloorPlanIds, floorPlans, generateKeysMut, pushUndo, showToast, t]);

  // --- Key 전체 초기화 (선택된 도면의 라인/구역 Key 를 한 번에 비움, 자동 입력 스코프와 동일) ---
  const [showClearKeysDialog, setShowClearKeysDialog] = useState(false);

  // 선택된 도면에서 비울 수 있는(suffix 가 채워진) Key 개수 — 메뉴 비활성 판단 + 확인 문구
  const clearableKeyCount = useMemo(() => {
    const hasSuffix = (key: string) => {
      const parts = key.split("_");
      const suffix = parts.length > 2 ? parts.slice(2).join("_") : (parts[parts.length - 1] ?? "");
      return suffix.trim() !== "";
    };
    let count = 0;
    for (const cfg of localConfigs) {
      if (cfg.floorPlanId == null || !selectedFloorPlanIds.has(cfg.floorPlanId)) continue;
      for (const l of cfg.countingLines) if (hasSuffix(l.key)) count++;
      for (const z of cfg.intervalZones) if (hasSuffix(z.key)) count++;
    }
    return count;
  }, [localConfigs, selectedFloorPlanIds]);

  const handleClearAllKeys = useCallback(() => {
    // suffix(prefix 뒤 전체)만 비운다. prefix = 앞 두 세그먼트(suffixOf/isEmptySuffix 와 동일 경계).
    // lastIndexOf 방식은 'zone_default_A_1' 같은 다중 세그먼트 suffix 를 'zone_default_A_' 로만 깎아
    // 여전히 채워진 것으로 취급되므로 쓰지 않는다.
    const clearSuffix = (key: string, fallbackPrefix: string) => {
      const parts = key.split("_");
      return parts.length > 2 ? parts.slice(0, 2).join("_") + "_" : fallbackPrefix;
    };

    let cleared = 0;
    pushUndo();
    setLocalConfigs((prev) =>
      prev.map((cfg) => {
        if (cfg.floorPlanId == null || !selectedFloorPlanIds.has(cfg.floorPlanId)) return cfg;
        let changed = false;
        const countingLines = cfg.countingLines.map((l) => {
          const nextKey = clearSuffix(l.key, "line_default_");
          if (nextKey === l.key) return l; // 이미 빈 칸 — 스킵
          changed = true;
          cleared++;
          return { ...l, key: nextKey };
        });
        const intervalZones = cfg.intervalZones.map((z) => {
          const nextKey = clearSuffix(z.key, "zone_default_");
          if (nextKey === z.key) return z;
          changed = true;
          cleared++;
          return { ...z, key: nextKey };
        });
        return changed ? { ...cfg, countingLines, intervalZones } : cfg;
      }),
    );

    setShowClearKeysDialog(false);
    if (cleared === 0) {
      showToast({ type: "info", title: t("toast.clearKeysEmpty.title"), content: t("toast.clearKeysEmpty.content") });
      return;
    }
    showToast({ type: "success", title: t("toast.clearKeysSuccess.title"), content: t("toast.clearKeysSuccess.content", { count: cleared }) });
  }, [selectedFloorPlanIds, pushUndo, showToast, t]);

  // --- 메모 ---
  const memoCountMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const memo of effectiveMemos) {
      map.set(memo.cameraId, (map.get(memo.cameraId) ?? 0) + 1);
    }
    return map;
  }, [effectiveMemos]);

  // 메모는 전체 저장과 분리되어 동작 즉시 서버에 반영된다.
  // 화면은 낙관적 업데이트로 먼저 바뀌고, 실패하면 되돌린 뒤 토스트로 알린다.
  const handleMemoSave = useCallback((actions: MemoAction[]) => {
    if (actions.length === 0) return;
    updateCameraMemosMut.mutate(actions, {
      onError: () => {
        showToast({
          type: "error",
          title: t("toast.memoSaveError.title"),
          content: t("toast.memoSaveError.content"),
        });
      },
    });
  }, [updateCameraMemosMut, showToast, t]);

  const handleMemoClickFromCard = useCallback((cameraId: number) => {
    setSelectedCameraId(cameraId);
    setMemoOpen(true);
    setAllMemosOpen(false);
  }, []);


  // 전체 메모 리스트는 "이 페이지에서 선택할 수 있는 카메라"의 메모만 보여준다.
  //
  // 이 페이지의 카메라 목록은 도면에 배치된 카메라만 담는다. 그런데 전체 메모는 아무 조건 없이
  // 모든 메모를 보여줬고, 도면에 배치되지 않은 카메라(미분석캠 등)의 메모를 누르면 그 카메라를
  // 선택할 수 없어 자동 선택 effect 가 곧바로 목록 첫 카메라로 되돌렸다 — 사용자 눈엔 "엉뚱한
  // 카메라의 상세가 뜬다"로 보였다 (ctos: 미분석캠 메모를 눌렀는데 다른 분석 화각의 메모가 나타남).
  //
  // 카메라 목록과 같은 기준(filteredCamerasOnFloorPlan)을 쓰므로 "보이는 메모는 반드시 누를 수
  // 있다"가 성립한다. 목록에서 빠진 카메라의 메모는 카메라 매핑 페이지에서 그대로 볼 수 있다.
  const selectableCameraMemos = useMemo(() => {
    const selectableCameraIds = new Set(filteredCamerasOnFloorPlan.map((c) => c.id));
    return effectiveMemos.filter((m) => selectableCameraIds.has(m.cameraId));
  }, [effectiveMemos, filteredCamerasOnFloorPlan]);

  const handleAllMemoClick = useCallback((cameraId: number) => {
    setSelectedCameraId(cameraId);
    setDrawingRequest(null);
    setSelectedEventSource(null);
    setSelectedMemoId(null);
    setMemoPlacingMode(null);
  }, []);

  // 현재 카메라의 스냅샷에 배치된 메모들
  const memosOnSnapshot = useMemo(
    () => effectiveMemos.filter(
      (m) => m.cameraId === selectedCameraId && m.positionForFloorPlan != null,
    ),
    [effectiveMemos, selectedCameraId],
  );

  const [pendingMemoRemoval, setPendingMemoRemoval] = useState<CameraMemo | null>(null);

  const handleToggleMemoOnSnapshot = useCallback((memo: CameraMemo) => {
    if (memo.positionForFloorPlan != null) {
      // 이미 스냅샷에 있으면 도면에서 제거 확인 다이얼로그 표시 (camera-mapping 과 일관성)
      setPendingMemoRemoval(memo);
    } else {
      // 배치 모드 진입 — 도면만 보고 있으면 스냅샷 뷰로 전환
      if (viewMode === "floorPlan") setViewMode("snapshot");
      setMemoPlacingMode({ memoId: memo.id, cameraId: memo.cameraId });
    }
  }, [viewMode]);

  const handleConfirmMemoRemoval = useCallback(() => {
    if (!pendingMemoRemoval) return;
    handleMemoSave([
      { actionType: "update", data: { ...pendingMemoRemoval, positionForFloorPlan: null } },
    ]);
    setPendingMemoRemoval(null);
  }, [pendingMemoRemoval, handleMemoSave]);

  // 이벤트소스(라인/존) 선택 통합 헬퍼 — 사이드바 클릭과 캔버스 클릭이 동일 흐름으로 모이도록 (PO QA i)
  // 1) selectedEventSource 상태 갱신
  // 2) 진행 중이던 새 라인/존 생성 모드(drawingRequest) 클리어 — 하이라이트 vs 실제 그려지는 도구 불일치 방지
  // 3) 한쪽(스냅샷 or 도면) 좌표가 비어 있으면 자동으로 그쪽에 그리도록 drawingTarget 셋업
  const selectEventSource = useCallback(
    (cameraId: number, type: "line" | "zone", id: number) => {
      setSelectedCameraId(cameraId);
      setSelectedEventSource({ type, id });
      setDrawingRequest(null);

      const cfg = configByCameraId.get(cameraId);
      const target = type === "line"
        ? cfg?.countingLines.find((l) => l.id === id)
        : cfg?.intervalZones.find((z) => z.id === id);

      if (!target) {
        setFloorPlanDrawingTarget(null);
        setSnapshotDrawingTarget(null);
        return;
      }

      // PO QA 8차 bi: 카드 알림과 동일 임계치(유효 도형: line>=2 / zone>=3)로 판정.
      // 좌표가 1개뿐인 junk 상태도 "없음"으로 보고 그 면에 그리도록 유도 (>0 으로 보면 1좌표를 "있음"으로 오판).
      const minPts = type === "line" ? 2 : 3;
      const hasSnapshot = !!target.analysisConfig && target.analysisConfig.coordinates.length >= minPts;
      const hasFloorPlan = !!target.floorPlanConfig && target.floorPlanConfig.coordinates.length >= minPts;
      const name = target.analysisConfig?.name ?? "";

      // 보기 모드는 사용자가 고른 것만 유지한다 (개선 니즈 #7, 효진님 제보).
      // 예전에는 미배치 구역을 선택하면 자동으로 '둘 다'로 바꿨는데,
      // (a) 사용자가 고른 모드를 마음대로 덮어쓰고
      // (b) 모드 전환이 캔버스 리핏을 일으켜, 방금 그린 도형의 좌표 편집 진입을 무력화했다
      //     (네모 그리고 꼭짓점 옮기려 하면 편집이 안 되던 버그).
      // 유도는 배너(drawingTarget)로만 하고, 어느 면을 볼지는 사용자에게 맡긴다.
      if (hasSnapshot && !hasFloorPlan) {
        // 스냅샷에는 있고 도면에는 없음 → 도면에 그리도록 유도
        setFloorPlanDrawingTarget({ type, id, name });
        setSnapshotDrawingTarget(null);
      } else if (!hasSnapshot && hasFloorPlan) {
        // 도면에는 있고 스냅샷에는 없음 → 스냅샷에 그리도록 유도
        // F13: 단, 카메라에 실제 스냅샷이 없으면 유도 불필요 (그릴 곳 없음)
        const cam = cameras.find((c) => c.id === cameraId);
        if (cam && hasRealSnapshot(cam)) {
          setSnapshotDrawingTarget({ type, id, name });
          setFloorPlanDrawingTarget(null);
        } else {
          setFloorPlanDrawingTarget(null);
          setSnapshotDrawingTarget(null);
        }
      } else {
        setFloorPlanDrawingTarget(null);
        setSnapshotDrawingTarget(null);
      }
    },
    [configByCameraId, cameras],
  );

  const handleMemoPlaced = useCallback((memoId: number, position: { x: number; y: number }) => {
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;
    handleMemoSave([{ actionType: "update", data: { ...memo, positionForFloorPlan: position } }]);
    setMemoPlacingMode(null);
    setSelectedMemoId(memoId);
  }, [effectiveMemos, handleMemoSave]);

  const handleMemoMarkerClick = useCallback((memoId: number | null) => {
    if (memoId != null) {
      setMemoOpen(true);
      setSelectedEventSource(null);
      const memo = effectiveMemos.find((m) => m.id === memoId);
      if (memo) setSelectedCameraId(memo.cameraId);
    }
    setSelectedMemoId(memoId);
  }, [effectiveMemos]);

  const handleMemoDragEnd = useCallback((memoId: number, position: { x: number; y: number }) => {
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;
    handleMemoSave([{ actionType: "update", data: { ...memo, positionForFloorPlan: position } }]);
  }, [effectiveMemos, handleMemoSave]);

  const handleDeleteMemoFromSnapshot = useCallback((memoId: number) => {
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;
    // 즉시 제거하지 않고 확인 다이얼로그 표시 (camera-mapping 과 일관성)
    setPendingMemoRemoval(memo);
  }, [effectiveMemos]);

  const selectedCameraDisplayName = useMemo(() => {
    const camera = cameras.find((c) => c.id === selectedCameraId);
    if (!camera) return null;
    return getDisplayName(camera);
  }, [cameras, selectedCameraId, getDisplayName]);

  // --- 저장 ---
  const updateCountingLinesMut = useUpdateCountingLines(branchUid!, versionId);
  const updateIntervalZonesMut = useUpdateIntervalZones(branchUid!, versionId);
  const updateAnalysisNumberMut = useUpdateCameraAnalysisNumber(branchUid!, versionId);

  const handleSave = useCallback(async (): Promise<boolean> => {
    isSavingRef.current = true;

    // diff 계산: serverConfigs vs localConfigs
    const lineDatums: Parameters<typeof updateCountingLinesMut.mutateAsync>[0] = [];
    const zoneDatums: Parameters<typeof updateIntervalZonesMut.mutateAsync>[0] = [];

    for (const localCfg of localConfigs) {
      const serverCfg = serverConfigs.find((s) => s.cameraId === localCfg.cameraId);

      // counting lines diff
      const serverLineIds = new Set(serverCfg?.countingLines.map((l) => l.id) ?? []);
      for (const line of localCfg.countingLines) {
        if (line.id < 0) {
          lineDatums.push({ actionType: "create", data: { key: line.key, cameraId: line.cameraId, analysisConfig: line.analysisConfig, floorPlanConfig: line.floorPlanConfig } });
        } else {
          const serverLine = serverCfg?.countingLines.find((l) => l.id === line.id);
          if (serverLine && lineDiffersFromServer(line, serverLine)) {
            lineDatums.push({ actionType: "update", data: line });
          }
        }
      }
      const localLineIds = new Set(localCfg.countingLines.map((l) => l.id));
      for (const id of serverLineIds) {
        if (!localLineIds.has(id)) {
          const serverLine = serverCfg!.countingLines.find((l) => l.id === id)!;
          lineDatums.push({ actionType: "delete", data: serverLine });
        }
      }

      // interval zones diff
      const serverZoneIds = new Set(serverCfg?.intervalZones.map((z) => z.id) ?? []);
      for (const zone of localCfg.intervalZones) {
        if (zone.id < 0) {
          zoneDatums.push({ actionType: "create", data: { key: zone.key, cameraId: zone.cameraId, analysisConfig: zone.analysisConfig, floorPlanConfig: zone.floorPlanConfig } });
        } else {
          const serverZone = serverCfg?.intervalZones.find((z) => z.id === zone.id);
          if (serverZone && zoneDiffersFromServer(zone, serverZone)) {
            zoneDatums.push({ actionType: "update", data: zone });
          }
        }
      }
      const localZoneIds = new Set(localCfg.intervalZones.map((z) => z.id));
      for (const id of serverZoneIds) {
        if (!localZoneIds.has(id)) {
          const serverZone = serverCfg!.intervalZones.find((z) => z.id === id)!;
          zoneDatums.push({ actionType: "delete", data: serverZone });
        }
      }
    }

    // analysisNumber diff: localConfigs vs serverConfigs (카메라별)
    const analysisNumberUpdates: { cameraOrientationId: number; analysisNumber: string | null }[] = [];
    for (const localCfg of localConfigs) {
      const serverCfg = serverConfigs.find((s) => s.cameraId === localCfg.cameraId);
      const serverAnalysisNumber = serverCfg?.analysisNumber ?? null;
      const localAnalysisNumber = localCfg.analysisNumber;
      if (localAnalysisNumber !== serverAnalysisNumber) {
        analysisNumberUpdates.push({
          cameraOrientationId: localCfg.cameraId,
          analysisNumber: localAnalysisNumber,
        });
      }
    }

    try {
      let failedCount = 0;
      let totalCount = 0;
      // analysisNumber를 먼저 반영해야 이후 line/zone onSuccess의 refetch 시 올바른 값이 내려옴
      if (analysisNumberUpdates.length > 0) {
        const analysisSettled = await Promise.allSettled(
          analysisNumberUpdates.map((u) => updateAnalysisNumberMut.mutateAsync(u)),
        );
        for (const r of analysisSettled) {
          totalCount += 1;
          if (r.status === "rejected") {
            failedCount += 1;
            console.error("[AnalysisDraft] analysisNumber update failed:", r.reason);
          }
        }
      }
      const promises: Promise<unknown>[] = [];
      if (lineDatums.length > 0) promises.push(updateCountingLinesMut.mutateAsync(lineDatums));
      if (zoneDatums.length > 0) promises.push(updateIntervalZonesMut.mutateAsync(zoneDatums));
      // 메모는 여기서 저장하지 않는다 — 동작 시점에 이미 서버에 반영됨
      const settled = await Promise.allSettled(promises);
      for (const r of settled) {
        totalCount += 1;
        if (r.status === "rejected") {
          failedCount += 1;
          console.error("[AnalysisDraft] save item failed:", r.reason);
        }
      }
      // 모든 mutation 완료 후 쿼리 갱신 & localConfigs 동기화
      // saving 플래그와 serverConfigsRef를 먼저 리셋하고 refetch해야
      // 데이터 도착 시 sync effect가 localConfigs를 최신 서버 데이터로 갱신함
      isSavingRef.current = false;
      serverConfigsRef.current = null;
      await queryClient.refetchQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
      await queryClient.refetchQueries({ queryKey: [ANALYSIS_CONFIGS_QUERY_KEY, branchUid, versionId] });
      if (failedCount > 0) {
        showToast({
          type: "error",
          title: t("toast.savePartial.title"),
          content: t("toast.savePartial.content", { failed: failedCount, total: totalCount }),
        });
        return false;
      } else {
        showToast({ type: "success", title: t("toast.saveSuccess.title"), content: t("toast.saveSuccess.content") });
        return true;
      }
    } catch {
      isSavingRef.current = false;
      showToast({ type: "error", title: t("toast.saveError.title"), content: t("toast.saveError.content") });
      return false;
    }
  }, [
    localConfigs,
    serverConfigs,
    updateCountingLinesMut,
    updateIntervalZonesMut,
    updateAnalysisNumberMut,
    queryClient,
    branchUid,
    versionId,
    showToast,
    t,
  ]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const saveOk = await handleSave();
    if (!saveOk) {
      setIsSubmitting(false);
      return;
    }
    const branch = branches.find((b) => b.uid === branchUid);
    if (branch && currentUser) {
      try {
        await sendAnalysisSketchSubmittedMessageToSlack({
          clientName: branch.client.name,
          branchName: branch.name,
          userEmail: currentUser.email,
          userName: currentUser.name,
          url: window.location.href,
        });
        // 7차 aw: 제출 = 분석 초안 단계 완료 → AnalysisDrafted(4) transition (monotonic)
        if (branch.configStatus < BranchConfigStatus.AnalysisDrafted) {
          try {
            await updateBranchConfig({
              branchUid: branchUid!,
              branchVersionId: versionId,
              config: { status: BranchConfigStatus.AnalysisDrafted as unknown as LegacyBranchConfigStatus },
            });
            queryClient.invalidateQueries({ queryKey: [BRANCHES_QUERY_KEY] });
          } catch (statusErr) {
            console.error("[AnalysisDraft] configStatus update to AnalysisDrafted failed:", statusErr);
          }
        }
        showToast({ type: "success", title: t("toast.submitSuccess.title"), content: t("toast.submitSuccess.content") });
      } catch {
        showToast({ type: "error", title: t("toast.submitError.title"), content: t("toast.submitError.content") });
      }
    }
    setIsSubmitting(false);
  }, [isSubmitting, handleSave, branches, branchUid, currentUser, showToast, t, queryClient, versionId]);

  if (isCamerasLoading || isFloorPlansLoading || isConfigsLoading) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <LeftPanel>
        <SidebarHeader
          floorPlans={floorPlans}
          selectedFloorPlanIds={selectedFloorPlanIds}
          onFloorPlanFilterChange={setSelectedFloorPlanIds}
          floorPlanIdsWithCameras={floorPlanIdsWithCameras}
          analyzedCount={analyzedCameras.length}
          mappedCount={mappedCameras.length}
          {...summary}
          cameraFilter={cameraFilter}
          cameraFilterAvailability={filterAvailability}
          filteredCount={filteredCamerasOnFloorPlan.length}
          onCameraFilterChange={setCameraFilter}
          onValidate={() => setShowValidationDialog(true)}
          onGenerateKeys={handleGenerateKeys}
          isGeneratingKeys={isGeneratingKeys}
          onClearAllKeys={() => setShowClearKeysDialog(true)}
          clearAllKeysDisabled={clearableKeyCount === 0}
          onOpenAddAdjustGuide={() => setAddAdjustGuideOpen(true)}
          activeTab={allMemosOpen ? "memos" : "cameras"}
          onTabChange={(tab) => {
            if (tab === "memos") {
              setAllMemosOpen(true);
              setMemoOpen(false);
            } else {
              setAllMemosOpen(false);
              setMemoOpen(false);
            }
          }}
          localConfigs={localConfigs}
          cameras={cameras}
          getDisplayName={getDisplayName}
        />
        {allMemosOpen ? (
          <>
            <MemoPanelDivider />
            <AllMemosPanel
              memos={selectableCameraMemos}
              cameras={cameras}
              getDisplayName={getDisplayName}
              selectedCameraId={selectedCameraId}
              selectedMemoId={selectedMemoId}
              onSave={handleMemoSave}
              onClose={() => setAllMemosOpen(false)}
              onMemoClick={handleAllMemoClick}
              onMemoSelect={setSelectedMemoId}
              onToggleMemoPlacement={handleToggleMemoOnSnapshot}
              placementLabels={{ show: t("inlineMemo.addToSnapshot"), remove: t("inlineMemo.removeFromSnapshot") }}
              showHeader={false}
            />
          </>
        ) : memoOpen ? (
          <>
            <MemoPanelDivider />
            <MemoPanel
              memos={effectiveMemos}
              selectedCameraId={selectedCameraId}
              cameraDisplayName={selectedCameraDisplayName}
              selectedFloorPlanId={selectedCamera?.floorPlanId ?? null}
              selectedMemoId={selectedMemoId}
              placeIcon={<IcPickOnImg width={14} height={14} />}
              isPlacedOnContext={(memo) => memo.positionForFloorPlan != null}
              placementLabels={{ add: t("inlineMemo.addToSnapshot"), remove: t("inlineMemo.removeFromSnapshot") }}
              onMemoSelect={setSelectedMemoId}
              onSave={handleMemoSave}
              onClose={() => { setMemoOpen(false); setMemoPlacingMode(null); }}
              onToggleMemoOnFloorPlan={handleToggleMemoOnSnapshot}
            />
          </>
        ) : (
          <ScrollAreaWrapper>
            <ScrollArea><CameraList>
              {(() => {
                const renderCard = (camera: Camera) => {
                  const cfg = configByCameraId.get(camera.id);
                  return (
                    <CameraCard
                      key={camera.id}
                      camera={camera}
                      displayName={getDisplayName(camera)}
                      isAnalyzed={!getDisplayName(camera).startsWith("N")}
                      isDuplicateNumber={duplicateDisplayNames.has(getDisplayName(camera))}
                      emptyNumberLabel={t("label.emptyNumber")}
                      selected={camera.id === selectedCameraId}
                      selectedLineId={camera.id === selectedCameraId && selectedEventSource?.type === "line" ? selectedEventSource.id : null}
                      selectedZoneId={camera.id === selectedCameraId && selectedEventSource?.type === "zone" ? selectedEventSource.id : null}
                      countingLines={(cfg?.countingLines ?? []).filter(isLineMatchingFilter)}
                      intervalZones={(cfg?.intervalZones ?? []).filter(isZoneMatchingFilter)}
                      globalDuplicateKeys={globalDuplicateKeys}
                      crossCameraDuplicateNames={crossCameraDuplicateNames}
                      duplicateNameLabel={t("error.duplicateName")}
                      emptyNameLabel={t("error.emptyName")}
                      crossCameraDuplicateNameLabel={t("error.crossCameraDuplicateName")}
                      duplicateKeyLabel={t("error.duplicateKey")}
                      emptyKeyLabel={t("error.emptyKey")}
                      invalidKeyFormatLabel={t("error.invalidKeyFormat")}
                      memoCount={memoCountMap.get(camera.id) ?? 0}
                      missingFloorPlanLineHint={t("hint.missingFloorPlanLine")}
                      missingFloorPlanZoneHint={t("hint.missingFloorPlanZone")}
                      missingSnapshotLineHint={t("hint.missingSnapshotLine")}
                      missingSnapshotZoneHint={t("hint.missingSnapshotZone")}
                      onClick={() => handleCameraClick(camera)}
                      onMemoClick={() => handleMemoClickFromCard(camera.id)}
                      onUpdateLineName={(lineId, name) => handleUpdateLineName(camera.id, lineId, name)}
                      onUpdateLineKey={(lineId, key) => handleUpdateLineKey(camera.id, lineId, key)}
                      onDeleteLine={(lineId) => requestDeleteLine(camera.id, lineId)}
                      onUpdateZoneName={(zoneId, name) => handleUpdateZoneName(camera.id, zoneId, name)}
                      onUpdateZoneKey={(zoneId, key) => handleUpdateZoneKey(camera.id, zoneId, key)}
                      onDeleteZone={(zoneId) => requestDeleteZone(camera.id, zoneId)}
                      onSelectLine={(lineId) => selectEventSource(camera.id, "line", lineId)}
                      onSelectZone={(zoneId) => selectEventSource(camera.id, "zone", zoneId)}
                    />
                  );
                };

                if (filteredCamerasOnFloorPlan.length === 0) {
                  return <EmptyMessage>{t("empty.cameras")}</EmptyMessage>;
                }

                // 도면이 1개만 결과에 포함되면 평탄 리스트, 2개 이상이면 도면별 섹션 헤더 + 그룹
                if (groupedCamerasByFloorPlan.length <= 1) {
                  return filteredCamerasOnFloorPlan.map(renderCard);
                }

                return groupedCamerasByFloorPlan.map(({ floorPlan, cameras }) => (
                  <FloorPlanGroup key={floorPlan.id}>
                    <FloorPlanGroupHeader>
                      <Icon icon="ic_map" width={14} height={14} />
                      <FloorPlanGroupName>{floorPlan.name}</FloorPlanGroupName>
                      <CameraCountBadge>{cameras.length}</CameraCountBadge>
                    </FloorPlanGroupHeader>
                    {cameras.map(renderCard)}
                  </FloorPlanGroup>
                ));
              })()}
            </CameraList></ScrollArea>
          </ScrollAreaWrapper>
        )}
      </LeftPanel>
      <RightPanel>
        <TopOverlay ref={topOverlayRef}>
          <CanvasToolbar
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            bothLayout={bothLayout}
            onBothLayoutChange={setBothLayout}
            showLineZone={showLineZone}
            onShowLineZoneChange={setShowLineZone}
            onAddLine={handleAddLine}
            onAddZone={handleAddZone}
            hasSelectedCamera={selectedCameraId !== null}
            selectedCameraHasSnapshot={!!selectedCamera && hasRealSnapshot(selectedCamera)}
            showFloorPlan={showFloorPlan}
          />
          {selectedCamera && (
            <SelectedCameraBar>
              <BarField>
                <BarLabel>{t("field.analysisNumber")}</BarLabel>
                {!isAnalyzing && <BarPrefix>N</BarPrefix>}
                <BarInputGroup>
                  {/* 2-3: 고객사는 분석 번호 수정 불가 — 정적 표기로 편집 불가능 명시 */}
                  {isStaff ? (
                    <>
                      <AnalysisNumberInput
                        $hasError={isDuplicateNumber || isEmptyNumber}
                        value={selectedDisplayName.replace(/^N/, "")}
                        onChange={(e) => handleAnalysisNumberChange(e.target.value)}
                        placeholder=""
                      />
                      {isDuplicateNumber && (
                        <BarErrorText>{t("error.duplicateNumber")}</BarErrorText>
                      )}
                      {!isDuplicateNumber && isEmptyNumber && (
                        <BarErrorText>{t("error.emptyNumber")}</BarErrorText>
                      )}
                    </>
                  ) : (
                    <AnalysisNumberStatic>{selectedDisplayName.replace(/^N/, "")}</AnalysisNumberStatic>
                  )}
                </BarInputGroup>
              </BarField>
              {/* 2-3: 고객사는 '분석 대상에 포함' 토글 미노출 */}
              {isStaff && (
                <BarSwitch>
                  <BarSwitchLabel>{t("field.includeInAnalysis")}</BarSwitchLabel>
                  <Switch
                    size="small"
                    color="primary"
                    checked={isAnalyzing}
                    onCheckedChange={handleAnalysisToggle}
                  />
                </BarSwitch>
              )}
            </SelectedCameraBar>
          )}
          {selectedCameraId && (
            <InlineMemoSectionDivider>
              <InlineMemoSection
                memos={effectiveMemos}
                selectedCameraId={selectedCameraId}
                selectedMemoId={selectedMemoId}
                onMemoIdChange={setSelectedMemoId}
                onSave={handleMemoSave}
                onToggleMemoPlacement={handleToggleMemoOnSnapshot}
              />
            </InlineMemoSectionDivider>
          )}
        </TopOverlay>
        <CanvasStack $row={isSideBySide}>
        <CanvasArea
          $hidden={!showSnapshot}
          $extraBasis={showFloorPlan && !isSideBySide ? topOverlayHeight : 0}
        >
          {selectedCameraId ? (
            <SnapshotCanvas
              coordinateEditingFinishRef={coordinateEditingFinishRef}
              snapshotUrl={selectedCamera?.previewImageUrl}
              cameraId={selectedCameraId}
              countingLines={configByCameraId.get(selectedCameraId)?.countingLines ?? []}
              intervalZones={configByCameraId.get(selectedCameraId)?.intervalZones ?? []}
              drawingRequest={drawingRequest}
              drawingTarget={snapshotDrawingTarget}
              drawingActiveCanvas={drawingActiveCanvas}
              onDrawingClaim={handleDrawingClaim}
              onDrawingTargetComplete={handleSnapshotDrawingTargetComplete}
              onDrawingTargetCancel={() => setSnapshotDrawingTarget(null)}
              onDrawingCancel={() => setDrawingRequest(null)}
              selectedEventSource={selectedEventSource}
              onDrawingComplete={handleDrawingComplete}
              onLineCoordinatesChange={handleLineCoordinatesChange}
              onZoneCoordinatesChange={handleZoneCoordinatesChange}
              onSelectionChange={(sel) => {
                if (sel && selectedCameraId != null) {
                  // 사이드바 클릭과 동일한 흐름 — drawingRequest 클리어 + 누락된 쪽에 drawingTarget 자동 셋업
                  selectEventSource(selectedCameraId, sel.type, sel.id);
                  // 라인/존 선택 시 메모 패널 닫고 메모 선택 해제
                  setMemoOpen(false);
                  setSelectedMemoId(null);
                  setMemoPlacingMode(null);
                } else {
                  setSelectedEventSource(sel);
                }
              }}
              onRequestDelete={(type, id) => {
                if (selectedCameraId == null) return;
                if (type === "line") requestDeleteLine(selectedCameraId, id);
                else requestDeleteZone(selectedCameraId, id);
              }}
              onAddMemo={() => setMemoOpen(true)}
              showLineZone={showLineZone}
              zoomButtonsBottomOffset={showFloorPlan && !isSideBySide ? 12 : 60}
              memosOnSnapshot={memosOnSnapshot}
              memoPlacingMode={memoPlacingMode}
              selectedMemoId={selectedMemoId}
              onMemoPlaced={handleMemoPlaced}
              onMemoMarkerClick={handleMemoMarkerClick}
              onMemoDragEnd={handleMemoDragEnd}
              onDeleteMemoFromSnapshot={handleDeleteMemoFromSnapshot}
              topPadding={topOverlayHeight}
              bottomPadding={isSideBySide || !showFloorPlan ? bottomBarHeight : 0}
              verticalAlign={showFloorPlan && !isSideBySide ? "bottom" : "center"}
            />
          ) : (
            <EmptyCanvas>
              {showFloorPlan && camerasOnFloorPlanForViewer.length === 0
                ? t("empty.cameras")
                : t("empty.selectCamera")}
            </EmptyCanvas>
          )}
        </CanvasArea>
        {showFloorPlan && (
          <FloorPlanViewer
            coordinateEditingFinishRef={coordinateEditingFinishRef}
            floorPlans={floorPlans}
            viewerFloorPlanId={floorPlanForViewer?.id ?? null}
            onViewerFloorPlanChange={handleViewerFloorPlanChange}
            selectedCameraId={selectedCameraId}
            imageUrl={floorPlanForViewer?.imageUrl ?? null}
            topPadding={showSnapshot && !isSideBySide ? 0 : topOverlayHeight}
            bottomPadding={bottomBarHeight}
            verticalAlign={showSnapshot && !isSideBySide ? "top" : "center"}
            extraBasis={showSnapshot && !isSideBySide ? bottomBarHeight : 0}
            countingLines={selectedCameraId ? (configByCameraId.get(selectedCameraId)?.countingLines ?? []) : []}
            intervalZones={selectedCameraId ? (configByCameraId.get(selectedCameraId)?.intervalZones ?? []) : []}
            referenceCountingLines={referenceCountingLines}
            referenceIntervalZones={referenceIntervalZones}
            showLineZone={showLineZone}
            drawingTarget={floorPlanDrawingTarget}
            drawingRequest={drawingRequest}
            drawingActiveCanvas={drawingActiveCanvas}
            onDrawingClaim={handleDrawingClaim}
            onFloorPlanDrawingRequestComplete={handleFloorPlanDrawingRequestComplete}
            onFloorPlanDrawingComplete={handleFloorPlanDrawingComplete}
            onDrawingCancel={() => {
              const cancelled = floorPlanDrawingTarget;
              setFloorPlanDrawingTarget(null);
              // 4-3-2: 도면 위 신규 라인 그리기 취소 시, 도면/스냅샷 어디에도 좌표가 없는 pure-draft 라인/존 제거
              if (cancelled) {
                setLocalConfigs((prev) =>
                  prev.map((cfg) => ({
                    ...cfg,
                    countingLines: cfg.countingLines.filter((l) => {
                      if (l.id !== cancelled.id) return true;
                      const hasSnap = !!l.analysisConfig && l.analysisConfig.coordinates.length > 0;
                      const hasFp = !!l.floorPlanConfig;
                      return hasSnap || hasFp;
                    }),
                    intervalZones: cfg.intervalZones.filter((z) => {
                      if (z.id !== cancelled.id) return true;
                      const hasSnap = !!z.analysisConfig && z.analysisConfig.coordinates.length > 0;
                      const hasFp = !!z.floorPlanConfig;
                      return hasSnap || hasFp;
                    }),
                  })),
                );
              }
            }}
            selectedEventSource={selectedEventSource}
            onSelectionChange={(sel) => {
              if (sel && selectedCameraId != null) {
                // PO QA 8차: 캔버스(도면)에서 직접 선택해도 사이드바와 동일 흐름 —
                // 한쪽에만 존재하는 소스면 누락된 쪽에 drawingTarget 자동 셋업(유도 배너)
                selectEventSource(selectedCameraId, sel.type, sel.id);
                setMemoOpen(false);
                setSelectedMemoId(null);
                setMemoPlacingMode(null);
              } else {
                setSelectedEventSource(sel);
              }
            }}
            onFloorPlanLineCoordinatesChange={handleFloorPlanLineCoordinatesChange}
            onFloorPlanZoneCoordinatesChange={handleFloorPlanZoneCoordinatesChange}
            onRequestFloorPlanDelete={(type, id) => {
              if (!selectedCameraId) return;
              if (type === "line") requestDeleteLine(selectedCameraId, id);
              else requestDeleteZone(selectedCameraId, id);
            }}
            floorPlanMemoDiagrams={visibleFloorPlanDiagramsForCurrentFloor}
            floorPlanMemoTexts={visibleFloorPlanTextsForCurrentFloor}
            cameras={camerasOnFloorPlanForViewer}
            onCameraMarkerClick={handleCameraClick}
            onReferenceShapeClick={(t) => {
              // 도면에서 다른 카메라의 라인/구역 클릭 → 소속 스냅샷으로 이동 + 포커싱 (v1 복원)
              selectEventSource(t.cameraId, t.type, t.id);
              setMemoOpen(false);
              setSelectedMemoId(null);
              setMemoPlacingMode(null);
            }}
            cameraDisplayNames={nonAnalysisNumberMap}
            cameraIconSize={floorPlanForViewer?.iconSize}
            cameraLabelSize={floorPlanForViewer?.labelSize}
            onScaleCommit={handleViewerScaleCommit}
          />
        )}
        </CanvasStack>
        <DraftActionBar
          ref={bottomBarRef}
          onSave={handleSave}
          onSubmit={handleSubmit}
          isSaving={updateCountingLinesMut.isPending || updateIntervalZonesMut.isPending || updateAnalysisNumberMut.isPending || isSubmitting}
          hasValidationError={hasAnyValidationError}
          validationResult={validationResult}
          isCoordinateEditing={isCoordinateEditing}
          hasChanges={isDirty}
        />
      </RightPanel>
      {isGeneratingKeys && <KeyGenLoadingOverlay />}
      <KeyValidationDialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        localConfigs={localConfigs}
        cameras={cameras}
        getDisplayName={getDisplayName}
        clientUid={clientUid}
      />
      <DeleteLineZoneDialog
        target={deleteTarget}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <ClearAllKeysDialog
        open={showClearKeysDialog}
        count={clearableKeyCount}
        onConfirm={handleClearAllKeys}
        onCancel={() => setShowClearKeysDialog(false)}
      />
      {addAdjustGuideOpen && branchUid && (
        <AddAdjustGuideModal
          onClose={() => setAddAdjustGuideOpen(false)}
          branchUid={branchUid}
          branchVersionId={versionId}
        />
      )}
      <DeleteConfirmDialog
        open={pendingMemoRemoval !== null}
        title={t("dialog.removeMemoFromSnapshotTitle")}
        name=""
        messageKey=""
        message={t("dialog.removeMemoFromSnapshotMessage")}
        ns="camera-mapping"
        onConfirm={handleConfirmMemoRemoval}
        onCancel={() => setPendingMemoRemoval(null)}
      />
    </Container>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 480px;
  flex-shrink: 0;
  border-right: 1px solid rgba(143, 149, 178, 0.24);
`;

const MemoPanelDivider = styled.div`
  height: 1px;
  flex-shrink: 0;
  background: rgba(143, 149, 178, 0.24);
`;


const ScrollAreaWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;


const CameraList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 2px 12px 100px 24px;
`;

const FloorPlanGroup = styled.section`
  display: flex;
  flex-direction: column;
  /* 카드 간격을 단일 도면(평탄 리스트) 케이스의 CameraList gap(12px)과 일치시킴 */
  gap: 12px;
`;

const FloorPlanGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  color: ${token("colors.text.secondary")};
`;

const FloorPlanGroupName = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
`;

const CameraCountBadge = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${token("colors.text.secondary")};
  background: rgba(143, 149, 178, 0.16);
  border-radius: 10px;
  padding: 2px 6px;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 12px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

const RightPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const TopOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  /* (F17) 하단 여백: 메모 바 유무와 관계없이 아래쪽 도면 셀렉트와 일관된 간격 확보 */
  padding-bottom: 8px;
  /* TopOverlay 자체는 투명 — 자식 중 toolbar/camera-bar 만 opaque, memo bar 는 floating 카드라 뒤 캔버스가 비침 */
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

/**
 * 스냅샷과 도면을 담는 컨테이너. '둘 다' 모드에서 방향을 바꾼다 (개선 니즈 #4).
 * 가로 배치는 세로로 짧고 가로로 긴 화면에서 도면을 크게 볼 수 있게 한다.
 */
const CanvasStack = styled.div<{ $row?: boolean }>`
  display: flex;
  flex-direction: ${({ $row }) => ($row ? "row" : "column")};
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

const CanvasArea = styled.div<{ $hidden?: boolean; $extraBasis?: number }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  flex: 1 1 ${({ $extraBasis }) => ($extraBasis ? `${$extraBasis}px` : "0%")};
  min-height: 0;
  overflow: hidden;
`;

const EmptyCanvas = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

const SelectedCameraBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 9px 20px 8px;
  flex-shrink: 0;
  background: #fff;
`;

const BarField = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px;
`;

const BarLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  color: ${token("colors.text.secondary")};
  margin-right: 6px;
`;

const BarPrefix = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${token("colors.text.primary")};
  flex-shrink: 0;
`;

const BarInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100px;
`;

const AnalysisNumberInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  height: 24px;
  border: none;
  border-bottom: 2px solid ${({ $hasError }) => ($hasError ? "#FA332D" : "rgba(143, 149, 178, 0.32)")};
  background: transparent;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${token("colors.text.primary")};
  outline: none;
  padding: 0;

  &:focus {
    border-bottom-color: ${({ $hasError }) => ($hasError ? "#FA332D" : token("colors.action.active"))};
  }
`;

// 2-3: 고객사용 정적 표기 — input 모양 대신 일반 텍스트로 표시 (편집 불가능함을 시각적으로 명확화)
const AnalysisNumberStatic = styled.span`
  display: inline-block;
  width: 100%;
  height: 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${token("colors.text.primary")};
`;

const BarErrorText = styled.span`
  padding: 4px 0 0 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #FA332D;
`;

const BarSwitch = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
`;

const BarSwitchLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
`;

const InlineMemoSectionDivider = styled.div`
  margin: 8px 12px 0;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(143, 149, 178, 0.18);
  border-radius: 8px;
`;
